import React, { useState, useEffect, useRef } from 'react';
import { Ticket, Message, InternalComment } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import socketService from '../../services/socket';
import soundService from '../../services/soundService';
import CountrySelect from '../common/CountrySelect';
import CompanySuggestion from '../common/CompanySuggestion';
import DeviceSelector from '../common/DeviceSelector';
import ServiceWorkflow from '../service/ServiceWorkflow';
import FileUpload from '../common/FileUpload';
import QuickMessages from './QuickMessages';
import { isTicketClosed, getStatusDisplayName, getStatusColorClasses, CLOSED_STATUSES, getStatusOptions } from '../../utils/statusUtils';
import MessageContentRenderer from '../common/MessageContentRenderer';


interface TicketDetailProps {
  ticket: Ticket;
  onBack: () => void;
  onTicketUpdate: () => Promise<void>;
}

const TicketDetail: React.FC<TicketDetailProps> = ({ ticket: initialTicket, onBack, onTicketUpdate }) => {
  
  const { user } = useAuth();

  // Helper function to format agent names consistently
  const formatAgentName = (agent: any): string => {
    if (!agent) return 'Unknown Agent';
    
    // If we have firstName and lastName, use them
    if (agent.firstName && agent.lastName) {
      return `${agent.firstName} ${agent.lastName}`;
    }
    
    // If we have a name field that's a string, use it
    if (typeof agent.name === 'string' && agent.name.trim()) {
      return agent.name;
    }
    
    // If name is an object with a name property, use that
    if (typeof agent.name === 'object' && agent.name && (agent.name as any)?.name) {
      return (agent.name as any).name;
    }
    
    // Fallback to email or ID
    return agent.email || agent.id || 'Unknown Agent';
  };
  const [ticket, setTicket] = useState(initialTicket);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageHistory, setMessageHistory] = useState<{
    chatMessages: Message[],
    systemMessages: Message[],
    emailMessages: Message[],
    lastSyncTime: number
  }>({
    chatMessages: [],
    systemMessages: [],
    emailMessages: [],
    lastSyncTime: 0
  });
  const [internalComments, setInternalComments] = useState<InternalComment[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [emails, setEmails] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [agents, setAgents] = useState<any[]>([]);
  const [reassigning, setReassigning] = useState(false);
  const [activeTab, setActiveTab] = useState<'messages' | 'comments' | 'files' | 'emails'>('messages');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: initialTicket?.title || '',
    description: initialTicket?.description || '',
    priority: initialTicket?.priority || 'medium',
    status: initialTicket?.status || 'new',
    customerName: initialTicket?.customerName || '',
    customerEmail: initialTicket?.customerEmail || '',
    customerPhone: initialTicket?.customerPhone || '',
    customerCompany: initialTicket?.customerCompany || '',
    customerAddress: initialTicket?.customerAddress || '', // Keep for backward compatibility
    customerStreetAddress: initialTicket?.customerStreetAddress || '',
    customerCity: initialTicket?.customerCity || '',
    customerState: initialTicket?.customerState || '',
    customerZipCode: initialTicket?.customerZipCode || '',
    customerCountry: initialTicket?.customerCountry || '',
    customerType: initialTicket?.customerType || 'Standard',
    deviceModel: initialTicket?.deviceModel || '',
    deviceSerialNumber: initialTicket?.deviceSerialNumber || '',
  });
  const [saving, setSaving] = useState(false);
  const [customerTypes, setCustomerTypes] = useState<any[]>([]);
  const [deviceModels, setDeviceModels] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [customerStatus, setCustomerStatus] = useState<{
    isOnline: boolean;
    lastSeen: string | null;
    hasEverConnected: boolean;
  }>({
    isOnline: false,
    lastSeen: null,
    hasEverConnected: false
  });
  
  // Email mode state for agents
  const [isEmailMode, setIsEmailMode] = useState(false);
  const [emailModeReason, setEmailModeReason] = useState<string | null>(null);
  const [agentPresence, setAgentPresence] = useState<Map<string, boolean>>(new Map());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Device selection state for editing
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<any>(null);
  
  // Company autocomplete state for editing
  const [companySuggestions, setCompanySuggestions] = useState<Array<{ name: string; confidence: number; description?: string; id?: string }>>([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [companyInputTimeout, setCompanyInputTimeout] = useState<NodeJS.Timeout | null>(null);

  // ===== ROBUST MESSAGE MANAGEMENT SYSTEM =====
  
  /**
   * Comprehensive message state manager that maintains integrity across all operations
   */
  const updateMessageHistory = (
    newMessages: Message[] = [],
    type: 'chat' | 'system' | 'email' | 'full_sync' = 'full_sync',
    operation: 'add' | 'replace' | 'merge' = 'merge'
  ) => {
    console.log(`🔧 MESSAGE MANAGER: ${operation} ${type} messages`, newMessages.length);
    
    setMessageHistory(prev => {
      let updatedHistory = { ...prev };
      
      switch (type) {
        case 'chat':
          if (operation === 'add') {
            updatedHistory.chatMessages = [...prev.chatMessages, ...newMessages];
          } else if (operation === 'replace') {
            updatedHistory.chatMessages = newMessages;
          } else { // merge
            const existingIds = prev.chatMessages.map(m => m.id);
            const newChatMessages = newMessages.filter(m => !existingIds.includes(m.id));
            updatedHistory.chatMessages = [...prev.chatMessages, ...newChatMessages];
          }
          break;
          
        case 'system':
          if (operation === 'add') {
            updatedHistory.systemMessages = [...prev.systemMessages, ...newMessages];
          } else if (operation === 'replace') {
            updatedHistory.systemMessages = newMessages;
          } else { // merge
            const existingIds = prev.systemMessages.map(m => m.id);
            const newSystemMessages = newMessages.filter(m => !existingIds.includes(m.id));
            updatedHistory.systemMessages = [...prev.systemMessages, ...newSystemMessages];
          }
          break;
          
        case 'email':
          if (operation === 'add') {
            updatedHistory.emailMessages = [...prev.emailMessages, ...newMessages];
          } else if (operation === 'replace') {
            updatedHistory.emailMessages = newMessages;
          } else { // merge
            const existingIds = prev.emailMessages.map(m => m.id);
            const newEmailMessages = newMessages.filter(m => !existingIds.includes(m.id));
            updatedHistory.emailMessages = [...prev.emailMessages, ...newEmailMessages];
          }
          break;
          
        case 'full_sync':
        default:
          // Categorize all messages by type
          const chatMsgs = newMessages.filter(m => 
            m.messageType === 'text' || m.messageType === 'file' || m.messageType === 'image'
          );
          const systemMsgs = newMessages.filter(m => 
            m.messageType === 'system' || m.messageType === 'email_notification'
          );
          const emailMsgs = newMessages.filter(m => 
            m.messageType === 'email' // Note: email_history removed - emails are now only in dedicated tab
          );
          
          if (operation === 'replace') {
            updatedHistory.chatMessages = chatMsgs;
            updatedHistory.systemMessages = systemMsgs;
            updatedHistory.emailMessages = emailMsgs;
          } else { // merge or add
            // Merge without duplicates
            const existingChatIds = prev.chatMessages.map(m => m.id);
            const existingSystemIds = prev.systemMessages.map(m => m.id);
            const existingEmailIds = prev.emailMessages.map(m => m.id);
            
            updatedHistory.chatMessages = [
              ...prev.chatMessages,
              ...chatMsgs.filter(m => !existingChatIds.includes(m.id))
            ];
            updatedHistory.systemMessages = [
              ...prev.systemMessages,
              ...systemMsgs.filter(m => !existingSystemIds.includes(m.id))
            ];
            updatedHistory.emailMessages = [
              ...prev.emailMessages,
              ...emailMsgs.filter(m => !existingEmailIds.includes(m.id))
            ];
          }
          break;
      }
      
      updatedHistory.lastSyncTime = Date.now();
      
      // Rebuild unified messages array
      const allMessages = [
        ...updatedHistory.chatMessages,
        ...updatedHistory.systemMessages,
        ...updatedHistory.emailMessages
      ].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      
      console.log(`🔧 MESSAGE MANAGER RESULT:`, {
        chat: updatedHistory.chatMessages.length,
        system: updatedHistory.systemMessages.length,
        email: updatedHistory.emailMessages.length,
        total: allMessages.length
      });
      
      // Update the main messages state atomically
      setMessages(allMessages);
      
      return updatedHistory;
    });
  };

  /**
   * Add a single message safely without affecting existing messages
   */
  const addSingleMessage = (message: Message) => {
    console.log(`📝 ADDING SINGLE MESSAGE: ${message.messageType} - ${message.id}`);
    
    if (message.messageType === 'system' || message.messageType === 'email_notification') {
      updateMessageHistory([message], 'system', 'add');
    } else if (message.messageType === 'email') {
      updateMessageHistory([message], 'email', 'add');
    } else {
      updateMessageHistory([message], 'chat', 'add');
    }
  };

  /**
   * Safe message loader that preserves existing local messages when possible
   */
  const loadMessagesWithIntegrity = async () => {
    try {
      console.log('🔄 SAFE MESSAGE LOADING for ticket:', ticket?.id);
      console.log('🔄 Ticket status:', ticket?.status);
      console.log('🔄 Current messages state before load:', messages.length);
      
      setLoading(true);
      
      if (!ticket?.id) {
        console.log('❌ No ticket ID available');
        return;
      }
      console.log('📡 Making API call to getMessages...');
      const fetchedMessages = await apiService.getMessages(ticket.id);
      console.log('📥 Database messages loaded:', fetchedMessages?.length || 0);
      
      if (fetchedMessages && fetchedMessages.length > 0) {
        console.log('📝 Sample of fetched messages:');
        fetchedMessages.slice(0, 3).forEach((msg, i) => {
          console.log(`   ${i + 1}. [${msg.messageType}] ${msg.sender?.userType}: "${msg.content?.substring(0, 50)}"`);
        });
      } else {
        console.log('⚠️  NO MESSAGES FETCHED FROM DATABASE - This is the problem!');
        console.log('   Ticket ID:', ticket.id);
        console.log('   Ticket Status:', ticket.status);
        console.log('   API Response:', fetchedMessages);
      }
      
      // Use the robust message manager for initial load
      updateMessageHistory(fetchedMessages || [], 'full_sync', 'replace');
      
      console.log('✅ Message loading completed, final message count:', (fetchedMessages || []).length);
      
    } catch (error) {
      console.error('❌ Error loading messages:', error);
      console.error('❌ Error details:', error instanceof Error ? error.message : String(error));

      // Don't clear existing messages on error!
      console.log('🛡️ Preserving existing messages due to load error');
    } finally {
      setLoading(false);
    }
  };
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // AI related state
  const [aiEnabled, setAiEnabled] = useState<boolean>(true);
  const [aiToggling, setAiToggling] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState<boolean>(false);
  const [aiStatus, setAiStatus] = useState<{
    enabled: boolean;
    reason: string | null;
    changedBy: string | null;
    disabledAt: string | null;
  }>({
    enabled: true,
    reason: null,
    changedBy: null,
    disabledAt: null
  });
  const [showServiceWorkflow, setShowServiceWorkflow] = useState(false);
  const [workflow, setWorkflow] = useState<any>(null);
  const [loadingWorkflow, setLoadingWorkflow] = useState(false);
  
  // Dropdown states for clickable badges
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showAssignmentDropdown, setShowAssignmentDropdown] = useState(false);

  // Add typing state and timer for agent typing detection
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // File upload states for agents
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [showFileUpload, setShowFileUpload] = useState(false);

  // Update local ticket state when prop changes
  useEffect(() => {
    console.log('🔧 Debug - useEffect triggered with initialTicket:', initialTicket?.id);
    
    // Only update if this is a completely new ticket, not just an update to the same ticket
    if (initialTicket && (!ticket || initialTicket.id !== ticket.id)) {
      console.log('🔄 New ticket loaded, setting up initial state');
      setTicket(initialTicket);
      
      // Initialize AI status from ticket
      const ticketAiEnabled = initialTicket.aiEnabled !== false;
      setAiEnabled(ticketAiEnabled);
      setAiStatus({
        enabled: ticketAiEnabled,
        reason: initialTicket.aiDisabledReason || null,
        changedBy: initialTicket.aiDisabledBy || null,
        disabledAt: initialTicket.aiDisabledAt || null
      });
      
      setEditForm({
        title: initialTicket.title,
        description: initialTicket.description,
        priority: initialTicket.priority,
        status: initialTicket.status,
        customerName: initialTicket.customerName || '',
        customerEmail: initialTicket.customerEmail || '',
        customerPhone: initialTicket.customerPhone || '',
        customerCompany: initialTicket.customerCompany || '',
        customerAddress: initialTicket.customerAddress || '',
        customerStreetAddress: initialTicket.customerStreetAddress || '',
        customerCity: initialTicket.customerCity || '',
        customerState: initialTicket.customerState || '',
        customerZipCode: initialTicket.customerZipCode || '',
        customerCountry: initialTicket.customerCountry || '',
        customerType: initialTicket.customerType || 'Standard',
        deviceModel: initialTicket.deviceModel || '',
        deviceSerialNumber: initialTicket.deviceSerialNumber || '',
      });
    } else if (initialTicket && ticket && initialTicket.id === ticket.id) {
      // Same ticket - only update if there are meaningful differences to preserve message state
      console.log('🔧 Same ticket updated, preserving message state');
      // Don't overwrite the ticket state to prevent message component re-renders
    }
  }, [initialTicket]);

  useEffect(() => {
    // Don't run effects if ticket is not loaded
    if (!ticket) return;
    
    loadMessages();
    loadAgents();

    // Socket event handlers for real-time updates
    const handleTicketAssigned = (data: { ticketId: string; agentId: string; agentName?: string; assignedAt: string }) => {
      console.log('🎯 TicketDetail: Received ticket_assigned event:', data);
      if (data.ticketId === ticket.id) {
        console.log('🔄 Updating ticket assignment in TicketDetail');
        setTicket(prev => prev ? {
          ...prev,
          agentId: data.agentId,
          assignedAt: data.assignedAt
        } : prev);
        
        // Reload agents to get updated agent info
        loadAgents();
      }
    };

    const handleTicketStatusChanged = (data: { ticketId: string; status: string; assignedAgentId?: string; changedBy?: string }) => {
      console.log('🎯 TicketDetail: Received ticket_status_changed event:', data);
      if (data.ticketId === ticket.id) {
        console.log('🔄 Updating ticket status in TicketDetail');
        setTicket(prev => prev ? {
          ...prev,
          status: data.status as any,
          agentId: data.assignedAgentId || prev.agentId
        } : prev);
      }
    };

    // Set up socket listeners
    socketService.on('ticket_assigned', handleTicketAssigned);
    socketService.on('ticket_status_changed', handleTicketStatusChanged);

    loadDropdownOptions();
    loadWorkflow();
    if (user?.role === 'agent' || user?.userType === 'agent') {
      loadInternalComments();
      loadCustomerStatus();
      loadFiles();
      loadEmails();
    }
    
    // Join the ticket room for real-time updates
    socketService.joinTicket(ticket.id);
    
    // Listen for new messages
    const handleNewMessage = (data: { message: Message }) => {
      console.log('📨 Agent received new message via Socket.IO:', data.message);
      
      // Play sound notification for customer messages
      if (data.message.sender?.userType === 'customer') {
        console.log('🔊 Playing sound for customer message');
        soundService.playTicketUpdateSound();
      }
      
      // Use safe message addition that preserves existing messages
      addSingleMessage(data.message);

      // If this is a file message, refresh the files list
      if (data.message.messageType === 'file' || data.message.messageType === 'image') {
        console.log('📁 File message received, refreshing files list');
        loadFiles();
      }
    };

    // Listen for typing indicators
    const handleTyping = (data: { ticketId: string; isTyping: boolean }) => {
      if (ticket?.id && data.ticketId === ticket.id) {
        console.log('⌨️ Agent received typing indicator:', data);
        setIsTyping(data.isTyping);
      }
    };

    // Listen for ticket updates
    const handleTicketUpdate = (updatedTicket: any) => {
      console.log('🎫 Ticket updated:', updatedTicket);
      
      // Handle auto-claim notifications
      if (updatedTicket.autoClaimed) {
        console.log('🤖 Auto-claim detected:', updatedTicket);
        // You can add a toast notification here if needed
      }
      
      // If we have a locally generated summary, preserve it to prevent overwrite
      if (ticket?.resolutionSummary && updatedTicket?.resolutionSummary !== ticket.resolutionSummary) {
        console.log('📝 Preserving local summary during ticket update');
        return; // Don't refresh to preserve local summary
      }
      
      // Critical fix for message persistence:
      // If the update is just a status change (like customer closing chat),
      // update the local ticket state instead of refreshing everything
      if (updatedTicket.updates && ticket) {
        console.log('🔧 Updating local ticket state instead of full refresh to preserve messages');
        console.log('🔧 Status change from', ticket.status, 'to', updatedTicket.updates.status);
        
        setTicket(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            ...updatedTicket.updates,
            // Preserve any locally stored data
            resolutionSummary: prev.resolutionSummary || updatedTicket.updates.resolutionSummary
          };
        });
        
        // For status changes that might affect message visibility, reload messages
        if (updatedTicket.updates.status) {
          console.log('🔄 Status changed, reloading messages to ensure they persist');
          setTimeout(() => loadMessages(), 100);
        }
        
        return; // Don't call onTicketUpdate() which would reset everything
      }
      
      // Only call the parent update for major changes (assignment, etc.)
      onTicketUpdate();
    };

    // Listen for customer status changes
    const handleCustomerStatusChange = (data: { ticketId: string; isOnline: boolean; lastSeen: string }) => {
      if (ticket?.id && data.ticketId === ticket.id) {
        console.log('👤 Customer status changed:', data);
        const wasOnline = customerStatus.isOnline;
        
        setCustomerStatus(prev => ({
          ...prev,
          isOnline: data.isOnline,
          lastSeen: data.lastSeen,
          hasEverConnected: true
        }));

        // Trigger email mode activation when customer goes offline (real-time fix)
        if (wasOnline && !data.isOnline && !isEmailMode) {
          console.log('🔴 Customer went offline - activating email mode immediately');
          const isTicketClosed = CLOSED_STATUSES.includes(ticket.status);
          
          setIsEmailMode(true);
          setEmailModeReason('customer_offline');
          
          if (!isTicketClosed) {
            // Add system message to agent's view ONLY for active tickets
            const offlineMessage: Message = {
              id: `customer-offline-${Date.now()}`,
              ticketId: ticket.id,
              content: '📧 Customer is offline. Email mode activated - messages will be sent via email.',
              createdAt: new Date().toISOString(),
              messageType: 'system' as const,
              sender: {
                id: null,
                firstName: 'System',
                lastName: '',
                userType: 'system' as const
              }
            };
            
            console.log('👤 Adding offline message due to real-time status change');
            addSingleMessage(offlineMessage);
          }
        }
      }
    };

    // Listen for agent status changes
    const handleAgentStatusChange = (data: { agentId: string; isOnline: boolean; lastSeen: string }) => {
      console.log('🔧 TicketDetail: Agent status changed:', data);
      console.log('🔧 TicketDetail: Current agents before update:', agents);
      
      setAgentPresence(prev => {
        const updated = new Map(prev);
        updated.set(data.agentId, data.isOnline);
        console.log('🔧 TicketDetail: Updated agent presence map:', Array.from(updated.entries()));
        return updated;
      });
      
      // Also update the agents list with real-time status
      setAgents(prev => {
        const updated = prev.map(agent => 
          agent.id === data.agentId 
            ? { ...agent, isOnline: data.isOnline, lastSeen: data.lastSeen }
            : agent
        );
        console.log('🔧 TicketDetail: Updated agents list:', updated);
        return updated;
      });
    };

    // Listen for AI status changes
    const handleAiStatusChange = (data: { ticketId: string; enabled: boolean; reason?: string; changedBy: string }) => {
      if (ticket?.id && data.ticketId === ticket.id) {
        console.log('🤖 AI status changed:', data);
        setAiEnabled(data.enabled);
        setAiStatus({
          enabled: data.enabled,
          reason: data.reason || null,
          changedBy: data.changedBy,
          disabledAt: data.enabled ? null : new Date().toISOString()
        });
        
        // Update local AI status without full refresh to preserve messages
        console.log('🤖 AI status updated locally, preserving message state');
      }
    };

    // Listen for AI summary generation events
    const handleSummaryGenerated = (data: { 
      ticketId: string; 
      summary: string; 
      generatedAt: string; 
      modelVersion: string; 
      confidence: number; 
    }) => {
      if (ticket?.id && data.ticketId === ticket.id) {
        console.log('📝 AI summary generated via socket:', data);
        setTicket(prev => {
          if (!prev) return prev;
          console.log('📝 Updating ticket with summary:', data.summary);
          return {
            ...prev,
            resolutionSummary: data.summary,
            resolutionSummaryGeneratedAt: data.generatedAt,
            resolutionSummaryModelVersion: data.modelVersion
          };
        });
        
        // Reset the generating state to ensure UI is properly updated
        setGeneratingSummary(false);
        
        // Don't call onTicketUpdate() here as it overwrites local state
        // The socket event already updated the local state, no need to refresh from parent
        console.log('📝 Summary updated via socket, skipping parent refresh to prevent overwrite');
      }
    };

    // Listen for AI ticket details updates
    const handleTicketDetailsUpdated = (data: {
      ticketId: string;
      title: string;
      description: string;
      confidence: number;
      generatedAt: string;
      categoryId?: string;
      categoryName?: string;
    }) => {
      if (ticket?.id && data.ticketId === ticket.id) {
        console.log('🤖 AI ticket details updated via socket:', data);
        setTicket(prev => {
          if (!prev) return prev;
          console.log('🤖 Updating ticket details:', {
            oldTitle: prev.title,
            newTitle: data.title,
            oldDescription: prev.description,
            newDescription: data.description,
            oldCategory: prev.category,
            newCategoryId: data.categoryId,
            newCategoryName: data.categoryName
          });
          return {
            ...prev,
            title: data.title,
            description: data.description,
            ...(data.categoryId && { category: data.categoryId }),
            aiGeneratedDetails: {
              confidence: data.confidence,
              generatedAt: data.generatedAt
            }
          };
        });

        // Update the edit form if it's open
        setEditForm((prev: any) => ({
          ...prev,
          title: data.title,
          description: data.description
        }));
        
        // Update handled locally above, no need for parent refresh that would clear messages
        console.log('🤖 AI details updated locally, preserving message state');
      }
    };

    // Listen for workflow updates
    const handleWorkflowUpdate = (data: { ticketId: string; workflowId: string; workflow: any; updatedStep: any; updatedBy: any; updatedAt: string }) => {
      if (ticket?.id && data.ticketId === ticket.id) {
        console.log('🔄 Workflow update received:', data);
        setWorkflow(data.workflow);
      }
    };

    // Listen for ticket claimed events (including auto-claims)
    const handleTicketClaimed = (data: { 
      ticketId: string; 
      agent: any; 
      claimedAt: string; 
      handoffFromAI?: boolean; 
      autoClaimed?: boolean;
      autoAssignedViaMessage?: boolean;
    }) => {
      if (ticket?.id && data.ticketId === ticket.id) {
        console.log('👤 Ticket claimed:', data);
        
        if (data.autoClaimed) {
          if (data.autoAssignedViaMessage) {
            console.log('💬 Auto-assignment via message:', `${data.agent.firstName} ${data.agent.lastName} was auto-assigned when they started chatting`);
            // You could show a specific toast notification for message-based assignment
          } else {
            console.log('⌨️ Auto-assignment via typing:', `${data.agent.firstName} ${data.agent.lastName} was auto-assigned when they started typing`);
          }
        }
        
        // Update ticket assignment locally to preserve messages
        if (data.agent) {
          setTicket(prev => prev ? {
            ...prev,
            agentId: data.agent.id,
            agent: data.agent,
            claimedAt: data.claimedAt
          } : prev);
          console.log('🎯 Ticket assignment updated locally, preserving message state');
        }
      }
    };

    // Listen for email mode conversion events
    const handleTicketConvertedToEmail = (data: { ticketId: string; reason: string; timestamp: string }) => {
      console.log('📧 Received ticket_converted_to_email event:', data);
      console.log('📧 Current ticket ID:', ticket?.id);
      console.log('📧 Event ticket ID:', data.ticketId);
      console.log('📧 Ticket IDs match:', ticket?.id && data.ticketId === ticket.id);
      
      if (ticket?.id && data.ticketId === ticket.id) {
        console.log('📧 Setting email mode to true with reason:', data.reason);
        setIsEmailMode(true);
        setEmailModeReason(data.reason);
        
        // Don't add system messages to closed tickets!
        const isTicketClosed = CLOSED_STATUSES.includes(ticket.status);
        
        if (!isTicketClosed) {
          // Add system message to inform agent ONLY for active tickets
          const emailModeMessage: Message = {
            id: `email-mode-${Date.now()}`,
            ticketId: ticket.id,
            content: `📧 Email mode activated - ${data.reason === 'customer_offline' ? 'Customer is offline' : 'Customer disconnected'}. Messages will be sent via email.`,
            createdAt: new Date().toISOString(),
            messageType: 'system' as const,
            sender: {
              id: null,
              firstName: 'System',
              lastName: '',
              userType: 'system' as const
            }
          };
          
          addSingleMessage(emailModeMessage);
        } else {
          console.log('📋 Skipping system message for closed ticket');
        }
      }
    };

    socketService.on('new_message', handleNewMessage);
    socketService.on('user_typing', handleTyping);
    socketService.on('ticket_updated', handleTicketUpdate);
    socketService.on('ticket_claimed', handleTicketClaimed);
    socketService.on('customer_status_changed', handleCustomerStatusChange);
    socketService.on('agent_status_changed', handleAgentStatusChange);
    socketService.on('ai_status_changed', handleAiStatusChange);
    socketService.on('ticket_summary_generated', handleSummaryGenerated);
    socketService.on('ticket_details_updated', handleTicketDetailsUpdated);
    socketService.on('workflow_updated', handleWorkflowUpdate);
    socketService.on('ticket_converted_to_email', handleTicketConvertedToEmail);

    return () => {
      socketService.off('new_message', handleNewMessage);
      socketService.off('user_typing', handleTyping);
      socketService.off('ticket_updated', handleTicketUpdate);
      socketService.off('ticket_claimed', handleTicketClaimed);
      socketService.off('customer_status_changed', handleCustomerStatusChange);
      socketService.off('agent_status_changed', handleAgentStatusChange);
      socketService.off('ai_status_changed', handleAiStatusChange);
      socketService.off('ticket_summary_generated', handleSummaryGenerated);
      socketService.off('ticket_details_updated', handleTicketDetailsUpdated);
      socketService.off('workflow_updated', handleWorkflowUpdate);
      socketService.off('ticket_converted_to_email', handleTicketConvertedToEmail);
      socketService.off('ticket_assigned', handleTicketAssigned);
      socketService.off('ticket_status_changed', handleTicketStatusChanged);
      
      // For agents: Don't leave ticket rooms to maintain membership for red indicators
      // For customers: They should leave rooms when navigating away
      // Note: Agents need to stay in all claimed ticket rooms to receive new_message events
      // for red dot indicators on inactive tabs. Only customers should leave rooms.
      if (ticket?.id && user?.userType === 'customer') {
        console.log('🚪 TicketDetail: Customer leaving ticket room:', ticket.id);
        socketService.leaveTicket(ticket.id);
      } else if (ticket?.id) {
        console.log('🏢 TicketDetail: Agent staying in ticket room for red indicators:', ticket.id);
        // Agents don't leave - they stay in all claimed rooms for notifications
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id, user?.role]);

  // Scroll to top when component mounts or ticket changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' }); // Use auto for immediate scrolling
    setIsInitialLoad(true); // Reset initial load flag for new ticket
  }, [ticket?.id]);



  useEffect(() => {
    if (isInitialLoad) {
      // On initial load, don't scroll to bottom, just mark as no longer initial
      setIsInitialLoad(false);
      // Ensure page stays at top after initial load
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }, 100);
    } else {
      // Only scroll to bottom for subsequent message updates
      scrollToBottom();
    }
  }, [messages, isInitialLoad]);

  const loadMessages = loadMessagesWithIntegrity;

  const loadAgents = async () => {
    try {
      console.log('🔍 Current user:', user);
      console.log('🔍 User role:', user?.role);
      console.log('🔍 User permissions:', user?.permissions);
      
      if (user?.role === 'agent' || user?.userType === 'agent') {
        console.log('🔍 Loading agents for reassign dropdown...');
        const fetchedAgents = await apiService.getAgents();
        console.log('🔍 Fetched agents:', fetchedAgents);
        console.log('🔍 Agent count:', fetchedAgents.length);
        setAgents(fetchedAgents);
      } else {
        console.log('🔍 User is not an agent, skipping agent loading');
      }
    } catch (error) {
      console.error('❌ Error loading agents:', error);
      console.error('❌ Error details:', error instanceof Error ? error.message : String(error));
    }
  };



  const loadDropdownOptions = async () => {
    try {
      const dropdownOptions = await apiService.getDropdownOptions();
      
      // Load customer types, device models, and categories from dropdown options
      const activeCustomerTypes = dropdownOptions.customerTypes.filter(type => type.isActive !== false);
      const activeDeviceModels = dropdownOptions.deviceModels.filter(model => model.isActive !== false);
      
      setCustomerTypes(activeCustomerTypes);
      setDeviceModels(activeDeviceModels);
      setCategories(dropdownOptions.categories || []);
    } catch (error) {
      console.error('Error loading dropdown options:', error);
      // Set default fallback options if API fails
      setCustomerTypes([
        { id: 'standard', name: 'Standard' },
        { id: 'vip', name: 'VIP' },
        { id: 'distributor', name: 'Distributor' }
      ]);
      setDeviceModels([
        { id: 'bwiii', name: 'BWIII' },
        { id: 'bwmini', name: 'BWMini' },
        { id: 'compass', name: 'Compass' },
        { id: 'maxxi', name: 'Maxxi' }
      ]);
      // Set default fallback categories
      setCategories([
        { id: 'general', name: 'General' },
        { id: 'technical', name: 'Technical' },
        { id: 'billing', name: 'Billing' },
        { id: 'support', name: 'Support' },
        { id: 'feature-request', name: 'Feature Request' },
        { id: 'bug-report', name: 'Bug Report' }
      ]);
    }
  };

  const loadInternalComments = async () => {
    if (!ticket?.id) return;
    
    try {
      const fetchedComments = await apiService.getInternalComments(ticket.id);
      setInternalComments(fetchedComments || []);
    } catch (error) {
      console.error('❌ Error loading internal comments:', error);
      setInternalComments([]);
    }
  };

  const loadCustomerStatus = async () => {
    if (!ticket?.id) return;
    
    try {
      console.log('👤 LOADING CUSTOMER STATUS for ticket:', ticket?.id);
      console.log('👤 Current message count before status load:', messages.length);
      
      const status = await apiService.getCustomerStatus(ticket.id);
      console.log('🔍 TicketDetail: Received customer status:', status);
      
      // Previous status for comparison
      const wasOnline = customerStatus.isOnline;
      
      const hasEverConnected = status.customerId !== null || status.lastSeen !== null;
      console.log('👤 Customer connection analysis:');
      console.log('   customerId:', status.customerId);
      console.log('   lastSeen:', status.lastSeen);
      console.log('   hasEverConnected:', hasEverConnected);
      
      setCustomerStatus({
        isOnline: status.isOnline,
        lastSeen: status.lastSeen,
        hasEverConnected: hasEverConnected
      });
      
      // Activate email mode when customer goes offline or is offline
      // BUT ONLY for active tickets - don't add system messages to closed tickets!
      const isTicketClosed = CLOSED_STATUSES.includes(ticket.status);
      
      if (!status.isOnline && !isEmailMode && !isTicketClosed) {
        console.log('🔴 Customer is offline - activating email mode for agent');
        setIsEmailMode(true);
        setEmailModeReason('customer_offline');
        
        // Add system message to agent's view ONLY for active tickets
        const offlineMessage: Message = {
          id: `customer-offline-${Date.now()}`,
          ticketId: ticket.id,
          content: '📧 Customer is offline. Email mode activated - messages will be sent via email.',
          createdAt: new Date().toISOString(),
          messageType: 'system' as const,
          sender: {
            id: null,
            firstName: 'System',
            lastName: '',
            userType: 'system' as const
          }
        };
        

        console.log('👤 Adding offline message to existing messages. Current count:', messages.length);
        addSingleMessage(offlineMessage);
        console.log('👤 After adding offline message, count should be:', messages.length + 1);
      } else if (isTicketClosed) {
        // For closed tickets, just activate email mode without adding system messages
        console.log('📋 Ticket is closed - activating email mode without adding system message');
        console.log('📋 Current message count for closed ticket:', messages.length);

        setIsEmailMode(true);
        setEmailModeReason('ticket_closed');
      }
      
      console.log('🔍 TicketDetail: Updated customerStatus state:', {
        isOnline: status.isOnline,
        lastSeen: status.lastSeen,
        hasEverConnected: status.customerId !== null || status.lastSeen !== null,
        emailMode: !status.isOnline
      });
    } catch (error) {
      console.error('❌ Error loading customer status:', error);
    }
  };

  const loadEmails = async () => {
    try {
      console.log('📧 Loading emails for ticket:', ticket?.id);
      if (!ticket?.id) {
        console.log('❌ No ticket ID available for loading emails');
        setEmails([]);
        return;
      }

      const emails = await apiService.getTicketEmails(ticket.id);
      setEmails(emails || []);
      console.log('📧 Emails loaded:', emails?.length || 0);
      
      // IMPORTANT: Only integrate emails into messages timeline if we're not already in the middle of loading messages
      if (loading) {
        console.log('📧 Messages are still loading, skipping email integration to prevent interference');
        return;
      }
      
      // Emails are now displayed only in the dedicated Emails tab
      // No longer integrating emails into the messages timeline to avoid duplicates
      console.log('📧 Emails loaded for dedicated tab display:', emails?.length || 0);
    } catch (error) {
      console.error('❌ Error loading emails:', error);
      setEmails([]);
    }
  };

  const loadFiles = async () => {
    if (!ticket?.id) {
      console.log('📁 Cannot load files: ticket.id is missing', { ticket });
      return;
    }
    
    try {
      console.log('📁 Loading files for ticket:', ticket.id);
      console.log('📁 User context:', { user: user, role: user?.role, userType: user?.userType });
      const ticketFiles = await apiService.getTicketFiles(ticket.id);
      console.log('📁 Loaded files response:', ticketFiles);
      console.log('📁 Files count:', ticketFiles?.length || 0);
      setFiles(ticketFiles || []);
    } catch (error) {
      console.error('❌ Error loading files:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        ticketId: ticket?.id,
        userRole: user?.role,
        userType: user?.userType
      });
      setFiles([]);
    }
  };

  const loadWorkflow = async () => {
    if (!ticket?.id) return;
    
    try {
      setLoadingWorkflow(true);
      console.log('🔧 Loading workflow for ticket:', ticket.id);
      
      // First, get list of workflows for this ticket
      const workflowsResponse = await apiService.get(`/tickets/${ticket.id}/service-workflows`);
      console.log('🔧 Workflows response:', workflowsResponse);
      
      if (workflowsResponse && Array.isArray(workflowsResponse) && workflowsResponse.length > 0) {
        // Get the full workflow details for the first workflow
        const workflowId = workflowsResponse[0].workflow_id; // Use snake_case as returned by database
        console.log('🔧 Loading workflow details for:', workflowId);
        
        const workflowDetails = await apiService.get(`/service-workflows/${workflowId}`);
        console.log('🔧 Workflow details:', workflowDetails);
        
        setWorkflow(workflowDetails);
      } else {
        setWorkflow(null);
      }
    } catch (error: any) {
      console.error('🔧 Error loading workflow:', error);
      // Don't show error for 404 - just means no workflow exists
      if (error.response?.status !== 404) {
        console.error('🔧 Unexpected error loading workflow:', error);
      }
      setWorkflow(null);
    } finally {
      setLoadingWorkflow(false);
    }
  };

  const scrollToBottom = () => {
    // Only scroll within the messages container, not the entire page
    if (messagesEndRef.current) {
      const messagesContainer = messagesEndRef.current.closest('.overflow-y-auto');
      if (messagesContainer) {
        messagesContainer.scrollTo({
          top: messagesContainer.scrollHeight,
          behavior: 'smooth'
        });
      } else {
        // Fallback: use scrollIntoView but with block: 'nearest' to minimize page scroll
        messagesEndRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }
  };

  // Handle message input change with typing detection
  const handleMessageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewMessage(value);

    // Only trigger typing for agents
    if (user?.role === 'agent' || user?.userType === 'agent') {
      // Start typing if not already typing
      if (!isAgentTyping && value.length > 0) {
        setIsAgentTyping(true);
        if (ticket?.id) {
          console.log('🔤 Agent started typing, emitting typing_start');
          socketService.startTyping(ticket.id);
        }
      }

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set timeout to stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        if (isAgentTyping) {
          setIsAgentTyping(false);
          if (ticket?.id) {
            console.log('🔤 Agent stopped typing, emitting typing_stop');
            socketService.stopTyping(ticket.id);
          }
        }
      }, 2000);
    }
  };

  // Stop typing when message is sent
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !ticket?.id) return;

    // Clear typing state immediately when sending
    if (isAgentTyping) {
      setIsAgentTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      socketService.stopTyping(ticket.id);
    }

    const messageContent = newMessage.trim();
    setNewMessage('');

    try {
      setSendingMessage(true);
      
      if (isEmailMode) {
        console.log('📧 Email mode: Sending email only (no chat balloon) for ticket:', ticket.id);
        
        // In email mode, only send the email - don't save to chat or show blue balloons
        // The email confirmation message will be created in the backend and sent via socket
        const emailResult = await apiService.sendEmail(ticket.id, messageContent);
        console.log('📧 Email sent successfully:', emailResult);
      } else {
        console.log('📤 Sending message to ticket:', ticket.id);
        await apiService.sendMessage(ticket.id, messageContent);
        console.log('📤 Message sent successfully');
      }
    } catch (error) {
      console.error('❌ Error sending message:', error);
      setNewMessage(messageContent); // Restore message on error
    } finally {
      setSendingMessage(false);
    }
  };

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Cleanup company input timeout on unmount
  useEffect(() => {
    return () => {
      if (companyInputTimeout) {
        clearTimeout(companyInputTimeout);
      }
    };
  }, [companyInputTimeout]);

  // Update edit form when ticket data changes (crucial for device fields)
  useEffect(() => {
    if (ticket) {
      console.log('🔄 useEffect: Updating edit form with latest ticket data');
      console.log('  - ticket.deviceModel:', ticket.deviceModel);
      console.log('  - ticket.deviceSerialNumber:', ticket.deviceSerialNumber);
      console.log('  - ticket.linkedDeviceId:', ticket.linkedDeviceId);
      console.log('  - isEditing:', isEditing);
      console.log('  - editForm.deviceModel before update:', editForm.deviceModel);
      console.log('  - editForm.deviceSerialNumber before update:', editForm.deviceSerialNumber);   
      setEditForm(prevForm => ({
        ...prevForm,
        title: ticket.title || '',
        description: ticket.description || '',
        priority: ticket.priority || 'medium',
        status: ticket.status || 'new',
        customerName: ticket.customerName || '',
        customerEmail: ticket.customerEmail || '',
        customerPhone: ticket.customerPhone || '',
        customerCompany: ticket.customerCompany || '',
        customerAddress: ticket.customerAddress || '',
        customerStreetAddress: ticket.customerStreetAddress || '',
        customerCity: ticket.customerCity || '',
        customerState: ticket.customerState || '',
        customerZipCode: ticket.customerZipCode || '',
        customerCountry: ticket.customerCountry || '',
        customerType: ticket.customerType || 'Standard',
        deviceModel: ticket.deviceModel || '',
        deviceSerialNumber: ticket.deviceSerialNumber || '',
      }));
      
      // Also update selectedDevice if there's a linkedDeviceId
      if (ticket.linkedDeviceId && ticket.deviceModel && ticket.deviceSerialNumber) {
        setSelectedDevice({
          id: ticket.linkedDeviceId,
          model: ticket.deviceModel,
          serialNumber: ticket.deviceSerialNumber
        });
        console.log('🔄 useEffect: Updated selectedDevice from ticket data:', {
          id: ticket.linkedDeviceId,
          model: ticket.deviceModel,
          serialNumber: ticket.deviceSerialNumber
        });
      }
    }
  }, [ticket]); // Re-run when ticket changes

  // Quick Messages handlers
  const handleQuickSendMessage = async (messageContent: string) => {
    if (!ticket?.id) return;

    // Clear typing state immediately when sending
    if (isAgentTyping) {
      setIsAgentTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      socketService.stopTyping(ticket.id);
    }

    try {
      setSendingMessage(true);
      
      if (isEmailMode) {
        console.log('📧 Email mode: Saving quick message to chat AND sending email for ticket:', ticket.id);
        
        // First, save the message to the chat conversation
        const sentMessage = await apiService.sendMessage(ticket.id, messageContent);
        console.log('📤 Quick message saved to chat successfully:', sentMessage);
        
        // Add message to local state (if not already added by Socket.IO)
        setMessages(prev => {
          if (prev.find(m => m.id === sentMessage.id)) {
            return prev;
          }
          return [...prev, sentMessage];
        });
        
        // Then, send it as an email
        const emailResult = await apiService.sendEmail(ticket.id, messageContent);
        console.log('📧 Quick email sent successfully:', emailResult);
        
        // Add email confirmation message to agent's view
        if (emailResult.emailSent) {
          const emailConfirmation: Message = {
            id: `quick-email-sent-${Date.now()}`,
            ticketId: ticket.id,
            content: `📧 Quick email sent to customer: "${messageContent}"`,
            createdAt: new Date().toISOString(),
            messageType: 'system' as const,
            sender: {
              id: null,
              firstName: 'System',
              lastName: '',
              userType: 'system' as const
            }
          };
          
          addSingleMessage(emailConfirmation);
        }
      } else {
        console.log('📤 Sending quick message to ticket:', ticket.id);
        await apiService.sendMessage(ticket.id, messageContent);
        console.log('📤 Quick message sent successfully');
      }
    } catch (error) {
      console.error('❌ Error sending quick message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleQuickInsertMessage = (messageContent: string) => {
    // Insert the message content into the text input
    setNewMessage(prev => {
      const currentValue = prev.trim();
      if (currentValue) {
        return currentValue + ' ' + messageContent;
      }
      return messageContent;
    });
  };

  // File upload handler for agents
  const handleFileSelect = async (files: FileList) => {
    if (!ticket?.id) {
      console.log('❌ Cannot upload: no ticket ID');
      return;
    }

    console.log('📎 Agent starting file upload for ticket:', ticket.id);
    const fileArray = Array.from(files);
    setUploadingFiles(fileArray);

    try {
      for (const file of fileArray) {
        console.log('📎 Uploading file:', file.name, 'size:', file.size, 'type:', file.type);
        
        const result = await apiService.uploadFile(ticket.id, file);
        console.log('📎 File uploaded successfully:', result);

        // Add the file message to local state (if not already added by Socket.IO)
        setMessages(prev => {
          if (prev.find(m => m.id === result.message.id)) {
            return prev;
          }
          return [...prev, result.message];
        });

        // Refresh files list in the files tab
        await loadFiles();
      }
    } catch (error) {
      console.error('❌ Error uploading files:', error);
      alert(`Failed to upload files: ${error instanceof Error ? error.message : String(error)}. Please try again.`);
    } finally {
      setUploadingFiles([]);
      setShowFileUpload(false);
    }
  };



  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !ticket?.id) return;

    const commentContent = newComment.trim();
    setNewComment('');

    try {
      setAddingComment(true);
      console.log('💬 Agent adding internal comment to ticket:', ticket.id);
      console.log('💬 Comment content:', commentContent);
      console.log('💬 Current user:', user);
      
      const addedComment = await apiService.addInternalComment(ticket.id, commentContent);
      console.log('💬 Internal comment added successfully:', addedComment);
      
      // Add comment to local state
      setInternalComments(prev => [...prev, addedComment]);
    } catch (error) {
      console.error('❌ Error adding internal comment:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        ticketId: ticket?.id,
        commentContent,
        user
      });
      setNewComment(commentContent); // Restore comment on error
      
      // Show more specific error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to add comment: ${errorMessage}`);
    } finally {
      setAddingComment(false);
    }
  };

  // Helper function to determine if the claim button should be shown
  const shouldShowClaimButton = () => {
    // Only show for agents with edit permissions
    if (!user?.permissions?.includes('tickets.edit')) {
      return false;
    }
    
    // Don't show if ticket data is incomplete (may be stale)
    if (!ticket?.id || !ticket?.ticketNumber) {
      console.warn('⚠️ Hiding claim button - ticket data incomplete:', { 
        hasId: !!ticket?.id, 
        hasTicketNumber: !!ticket?.ticketNumber 
      });
      return false;
    }
    
    // Don't show if ticket is already assigned to the current user
    if (ticket.agentId === user?.id) {
      return false;
    }
    
    // Show if ticket is unassigned
    if (!ticket.agentId) {
      return true;
    }
    
    // Show if ticket is assigned to AI agent (allow human takeover)
    if (ticket.agent) {
      const agentName = formatAgentName(ticket.agent);
      if (agentName.includes('NeuroAI') || agentName.includes('AI')) {
        return true;
      }
    }
    
    // Don't show if assigned to another human agent
    return false;
  };

  const handleClaimTicket = async () => {
    if (!ticket?.id) {
      console.error('❌ No ticket ID available for claim');
      return;
    }
    
    try {
      console.log('🎯 Attempting to claim ticket:', {
        ticketId: ticket.id,
        ticketNumber: ticket.ticketNumber,
        currentAgent: ticket.agentId,
        userPermissions: user?.permissions?.includes('tickets.edit')
      });
      
      // Validate ticket data before attempting claim
      if (!ticket.ticketNumber) {
        console.warn('⚠️ Warning: Ticket missing ticketNumber, data may be stale');
      }
      
      const result = await apiService.claimTicket(ticket.id);
      console.log('✅ Ticket claimed successfully:', result);
      
      // Validate the response structure
      if (!result || typeof result !== 'object') {
        throw new Error('Invalid response from claim ticket API');
      }
      
      // Update ticket assignment locally instead of full refresh to preserve messages
      // Use agent information returned from the API for consistency
      setTicket(prev => prev ? {
        ...prev,
        agentId: result.agentId || user?.id,
        status: result.status || 'in_progress_tier1',
        // Use agent info from API response, fallback to current user
        agent: result.agent || {
          id: user?.id || '',
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
          email: user?.email || '',
          role: 'agent' as const
        }
      } : prev);
      console.log('✅ Ticket claim updated locally, preserving message state');
      
      // Immediately trigger tab refresh via onTicketUpdate callback
      if (onTicketUpdate) {
        console.log('🚀 TicketDetail: Triggering immediate tab refresh after claim');
        onTicketUpdate();
      }
      
      // Also emit a custom event for immediate tab bar refresh
      window.dispatchEvent(new CustomEvent('ticket-claimed', {
        detail: { ticketId: ticket.id, agentId: user?.id }
      }));
    } catch (error) {
      console.error('❌ Error claiming ticket:', error);
      
      // Show user-friendly error message
      if (error instanceof Error) {
        const errorMessage = error.message;
        if (errorMessage.includes('already assigned to another human agent')) {
          alert('This ticket has already been assigned to another agent.');
          // Don't refresh to preserve messages - the agent can see this info in the UI already
        } else if (errorMessage.includes('404') || errorMessage.includes('not found')) {
          alert('This ticket no longer exists or has been moved. Returning to ticket list...');
          // Go back to ticket list since this ticket doesn't exist
          onBack();
        } else {
          alert(`Failed to claim ticket: ${errorMessage}`);
          // Don't refresh to preserve messages - error is shown to user
        }
      } else {
        alert('Failed to claim ticket. Please check your permissions and try again.');
        // Don't refresh to preserve messages - error is shown to user
      }
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!ticket?.id) {
      console.error('❌ handleStatusUpdate: No ticket ID provided');
      return;
    }
    
    try {
      const oldStatus = ticket.status;
      console.log('🔧 handleStatusUpdate: Starting status update', { ticketId: ticket.id, oldStatus, newStatus });
      
      const response = await apiService.updateTicket(ticket.id, { status: newStatus as any });
      console.log('✅ handleStatusUpdate: API call successful', response);
      
      // Show notification for ticket reopening
      const closedStatuses = ['closed-resolved', 'closed-not-resolved', 'closed-partially-resolved', 'closed-no-feedback', 'closed-by-agent-resolved', 'abandoned'];
      const reopenStatuses = ['in_progress', 'open', 'reopened'];
      
      if (closedStatuses.includes(oldStatus) && reopenStatuses.includes(newStatus)) {
        if (ticket.agent && ticket.agentId) {
          alert(`Ticket reopened successfully! The assigned agent (${formatAgentName(ticket.agent)}) will receive an email notification about this reopened ticket.`);
        } else {
          alert('Ticket reopened successfully!');
        }
      }
      
      // Update status locally instead of full refresh to preserve messages
      setTicket(prev => {
        const updated = prev ? {
          ...prev,
          status: newStatus as any
        } : prev;
        console.log('🔧 handleStatusUpdate: Local state updated', { prev: prev?.status, updated: updated?.status });
        return updated;
      });
      console.log('📝 Status updated locally to preserve messages:', oldStatus, '→', newStatus);
      
      // Notify parent component of the update
      if (onTicketUpdate) {
        console.log('🔧 handleStatusUpdate: Calling onTicketUpdate callback');
        onTicketUpdate();
      }
      
      // Auto-generate summary when ticket is closed (only if no summary exists)
      if (isTicketClosed(newStatus as any) && user?.permissions?.includes('tickets.edit') && !ticket?.resolutionSummary) {
        console.log('🎯 Ticket closed, auto-generating summary...');
        setTimeout(() => {
          handleGenerateSummary();
        }, 2000); // Delay to ensure ticket is fully updated
      }
    } catch (error) {
      console.error('❌ Error updating ticket status:', error);
      // Show user-friendly error message
      alert('Failed to update ticket status. Please try again.');
    }
  };

  const handleReassignTicket = async (newAgentId: string) => {
    if (!ticket?.id) return;
    
    try {
      setReassigning(true);
      const updatedTicket = await apiService.reassignTicket(ticket.id, newAgentId);
      
      // Update local ticket state immediately
      setTicket(prev => ({
        ...prev,
        agentId: updatedTicket.agentId,
        agent: updatedTicket.agent
      }));
      
      setShowAssignmentDropdown(false);
      
      // Show success message with email notification info
      if (newAgentId) {
        const agentName = updatedTicket.agent ? formatAgentName(updatedTicket.agent) : 'the selected agent';
        alert(`Ticket successfully assigned to ${agentName}! They will receive both a system notification and an email with a direct link to this ticket.`);
      } else {
        alert('Ticket successfully unassigned.');
      }
      
      // Trigger immediate tab refresh for assignment changes
      try {
        await onTicketUpdate();
      } catch (error) {
        console.error('❌ Error refreshing parent component after ticket reassignment:', error);
        // Don't throw the error - the reassignment itself was successful
      }
      
      // Emit custom event for immediate tab bar refresh
      if (newAgentId === user?.id) {
        // User assigned ticket to themselves
        console.log('🚀 TicketDetail: Emitting ticket-claimed event for self-assignment');
        window.dispatchEvent(new CustomEvent('ticket-claimed', {
          detail: { ticketId: ticket.id, agentId: user?.id }
        }));
      } else if (ticket.agentId === user?.id && newAgentId !== user?.id) {
        // User unassigned ticket from themselves
        console.log('🚀 TicketDetail: Emitting ticket-unclaimed event for self-unassignment');
        window.dispatchEvent(new CustomEvent('ticket-unclaimed', {
          detail: { ticketId: ticket.id, agentId: user?.id }
        }));
      }
      
    } catch (error) {
      console.error('Error reassigning ticket:', error);
      alert('Failed to reassign ticket. Please try again.');
    } finally {
      setReassigning(false);
    }
  };

  const handleToggleAi = async (enabled: boolean, reason?: string) => {
    if (!ticket?.id) return;
    
    try {
      setAiToggling(true);
      console.log(`🤖 ${enabled ? 'Enabling' : 'Disabling'} AI for ticket ${ticket.ticketNumber}`);
      
      await apiService.toggleTicketAi(ticket.id, enabled, reason);
      
      // The socket event will update the state, but we can also update locally for immediate feedback
      setAiEnabled(enabled);
      console.log(`✅ AI ${enabled ? 'enabled' : 'disabled'} for ticket ${ticket.ticketNumber}`);
      
      // Trigger immediate tab refresh to reflect the change
      try {
        await onTicketUpdate();
      } catch (error) {
        console.error('❌ Error refreshing parent component after AI toggle:', error);
        // Don't throw the error - the AI toggle itself was successful
      }
      
    } catch (error) {
      console.error('Error toggling AI:', error);
      alert(`Failed to ${enabled ? 'enable' : 'disable'} AI. Please try again.`);
      // Revert local state on error
      setAiEnabled(!enabled);
    } finally {
      setAiToggling(false);
    }
  };

  const handleGenerateSummary = async () => {
    console.log('🔴 [DEBUG] handleGenerateSummary called');
    console.log('🔴 [DEBUG] ticket:', ticket);
    console.log('🔴 [DEBUG] ticket.id:', ticket?.id);
    console.log('🔴 [DEBUG] user permissions:', user?.permissions);
    console.log('🔴 [DEBUG] generatingSummary state:', generatingSummary);
    
    if (!ticket?.id) {
      console.error('🔴 [DEBUG] No ticket ID available');
      alert('Error: No ticket ID available');
      return;
    }
    
    // Prevent multiple simultaneous generation attempts
    if (generatingSummary) {
      console.log('🔴 [DEBUG] Summary generation already in progress, skipping');
      return;
    }
    
    try {
      console.log('🔴 [DEBUG] Setting generatingSummary to true');
      setGeneratingSummary(true);
      console.log(`📝 Generating summary for ticket ${ticket.ticketNumber} (ID: ${ticket.id})`);
      
      console.log('🔴 [DEBUG] Calling apiService.generateTicketSummary...');
      const summaryData = await apiService.generateTicketSummary(ticket.id);
      console.log('✅ Summary generated successfully:', summaryData);
      
      // Update local ticket state with new summary
      setTicket(prev => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          resolutionSummary: summaryData.summary,
          resolutionSummaryGeneratedAt: summaryData.generatedAt,
          resolutionSummaryModelVersion: summaryData.modelVersion,
          resolutionSummaryGeneratedBy: summaryData.generatedBy
        };
        console.log('🔴 [DEBUG] Updated ticket state:', updated);
        return updated;
      });
      
      // Don't call onTicketUpdate() immediately to avoid race condition
      // The Socket.IO event 'ticket_summary_generated' will handle real-time updates for all users
      console.log('🔴 [DEBUG] Summary updated locally, waiting for socket event confirmation');
      
      // The socket event will handle further updates, no need to refresh parent
      
    } catch (error: any) {
      console.error('❌ Error generating summary:', error);
      console.error('🔴 [DEBUG] Error type:', typeof error);
      console.error('🔴 [DEBUG] Error constructor:', error.constructor.name);
      console.error('🔴 [DEBUG] Error message:', error.message);
      console.error('🔴 [DEBUG] Error stack:', error.stack);
      
      // More detailed error handling
      let errorMessage = 'Failed to generate summary. ';
      
      // Check if it's a fetch error or API error
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage += 'Network error. Please check if the backend server is running.';
      } else if (error.message.includes('Authentication failed')) {
        errorMessage += 'Authentication failed. Please login again.';
      } else if (error.message.includes('HTTP error')) {
        errorMessage += `Server error: ${error.message}`;
      } else {
        errorMessage += `${error.message || 'Please try again or contact support.'}`;
      }
      
      alert(errorMessage);
      console.error('📋 Full error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        error: error
      });
    } finally {
      console.log('🔴 [DEBUG] Setting generatingSummary to false');
      setGeneratingSummary(false);
    }
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'customerAddress') {
      console.log('🔧 Address field changed:');
      console.log('  - name:', name);
      console.log('  - value:', value);
      console.log('  - value type:', typeof value);
      console.log('  - value length:', value.length);
    }
    
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'customerAddress') {
      console.log('🔧 EditForm updated, new state will have customerAddress:', value);
    }

    // Real-time email validation
    if (name === 'customerEmail') {
      if (value && !validateEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError('');
      }
    }
    
    // Handle company field changes for device loading and autocomplete
    if (name === 'customerCompany') {
      // Clear existing timeout
      if (companyInputTimeout) {
        clearTimeout(companyInputTimeout);
      }

      const companyName = value.trim();
      if (companyName.length >= 2) {
        // Set a debounced timeout to fetch suggestions
        const timeout = setTimeout(async () => {
          try {
            const response = await apiService.fuzzyMatchCompany(companyName, 75);
            if (response.matches.length > 0) {
              // Transform matches to match CompanySuggestion component expectations
              const suggestions = response.matches.map(match => ({
                name: match.name,
                confidence: match.score,
                description: match.aliases?.length > 0 ? `Also known as: ${match.aliases.join(', ')}` : undefined,
                id: match.id
              }));
              setCompanySuggestions(suggestions);
              setShowCompanySuggestions(true);

              // Auto-detect company if high confidence match
              const bestMatch = response.matches[0];
              if (bestMatch.score >= 90) {
                setSelectedCompanyId(bestMatch.id);
                console.log(`🏢 Auto-detected company: ${bestMatch.name} (ID: ${bestMatch.id})`);
              }
            } else {
              setShowCompanySuggestions(false);
              setSelectedCompanyId(null);
            }
          } catch (error) {
            console.error('Error fetching company suggestions:', error);
            setShowCompanySuggestions(false);
            setSelectedCompanyId(null);
          }
        }, 500); // 500ms delay

        setCompanyInputTimeout(timeout);
      } else {
        setShowCompanySuggestions(false);
        setSelectedCompanyId(null);
      }
    }
  };

  const handleCompanySelect = (companyName: string) => {
    setEditForm(prev => ({
      ...prev,
      customerCompany: companyName
    }));
    setShowCompanySuggestions(false);
    
    // Find the selected company ID
    const selectedSuggestion = companySuggestions.find(suggestion => suggestion.name === companyName);
    if (selectedSuggestion && selectedSuggestion.id) {
      setSelectedCompanyId(selectedSuggestion.id);
    }
  };

  const handleDismissCompanySuggestions = () => {
    setShowCompanySuggestions(false);
  };

  const handleDeviceSelect = (device: any) => {
    setSelectedDevice(device);
    if (device) {
      // Update form data with device information
      // device.model is already mapped to valid enum value by DeviceSelector
      setEditForm(prev => ({
        ...prev,
        deviceModel: device.model,
        deviceSerialNumber: device.serialNumber
      }));
    } else {
      // Clear device information
      setEditForm(prev => ({
        ...prev,
        deviceModel: '',
        deviceSerialNumber: ''
      }));
    }
  };

  const handleSaveEdit = async () => {
    setError('');
    setEmailError('');

    // Validate email if provided
    if (editForm.customerEmail && !validateEmail(editForm.customerEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    console.log('🔧 Debug - handleSaveEdit called');
    console.log('🔧 Debug - ticket:', ticket);
    console.log('🔧 Debug - initialTicket:', initialTicket);
    console.log('🔧 Debug - editForm:', editForm);
    console.log('🔧 Debug - editForm.customerAddress:', editForm.customerAddress);
    console.log('🔧 Debug - customerAddress type:', typeof editForm.customerAddress);
    console.log('🔧 Debug - customerAddress length:', editForm.customerAddress?.length);
    
    // Use initialTicket as fallback if local ticket state is undefined
    const ticketToUpdate = ticket || initialTicket;
    
    if (!ticketToUpdate?.id) {
      console.error('Cannot save edit: ticket ID is missing');
      console.error('ticket:', ticket);
      console.error('initialTicket:', initialTicket);
      alert('Error: Ticket ID is missing. Please refresh the page and try again.');
      return;
    }
    
    try {
      setSaving(true);
      console.log('🔧 Updating ticket with ID:', ticketToUpdate.id);
      console.log('🔧 Update data:', editForm);
      console.log('🔧 About to send customerAddress:', editForm.customerAddress);
      console.log('🔧 About to send customerType:', editForm.customerType);
      console.log('🔧 About to send deviceSerialNumber:', editForm.deviceSerialNumber);
            console.log('🔧 About to send deviceModel:', editForm.deviceModel);
      console.log('🔧 deviceModel type:', typeof editForm.deviceModel);
      console.log('🔧 Sending device model as-is without mapping to preserve configurable models');
      
              // Prepare update data with proper typing for deviceModel
        const updateData: Partial<Ticket> = {
          ...editForm,
          deviceModel: editForm.deviceModel,
          selectedDeviceId: selectedDevice?.id // Include selected device ID if one is chosen
        } as Partial<Ticket>;
        
        const updatedTicket = await apiService.updateTicket(ticketToUpdate.id, updateData);
      console.log('🔧 Update successful, received ticket:', updatedTicket);
      console.log('🔧 Received customerAddress:', updatedTicket.customerAddress);
      console.log('🔧 Received customerType:', updatedTicket.customerType);
      console.log('🔧 Received deviceModel:', updatedTicket.deviceModel);
      console.log('🔧 Received deviceSerialNumber:', updatedTicket.deviceSerialNumber);
      console.log('🔧 Received selectedDeviceId:', updatedTicket.selectedDeviceId);
      
      // Update the local ticket state immediately with the response from the server
      setTicket(updatedTicket);
      
      // Update the edit form to match the updated ticket (in case server modified any values)
              setEditForm({
          title: updatedTicket.title,
          description: updatedTicket.description,
          priority: updatedTicket.priority,
          status: updatedTicket.status,
          customerName: updatedTicket.customerName || '',
          customerEmail: updatedTicket.customerEmail || '',
          customerPhone: updatedTicket.customerPhone || '',
          customerCompany: updatedTicket.customerCompany || '',
          customerAddress: updatedTicket.customerAddress || '',
          customerStreetAddress: updatedTicket.customerStreetAddress || '',
          customerCity: updatedTicket.customerCity || '',
          customerState: updatedTicket.customerState || '',
          customerZipCode: updatedTicket.customerZipCode || '',
          customerCountry: updatedTicket.customerCountry || '',
          customerType: updatedTicket.customerType || 'Standard',
          deviceModel: updatedTicket.deviceModel || '',
          deviceSerialNumber: updatedTicket.deviceSerialNumber || '',
      });
      
      console.log('🔧 Updated editForm with new customerAddress:', updatedTicket.customerAddress);
      
      // Exit edit mode
      setIsEditing(false);
      
      // Notify parent component to refresh the ticket list
      console.log('🔧 Calling onTicketUpdate to refresh parent');
      try {
        await onTicketUpdate();
      } catch (error) {
        console.error('❌ Error refreshing parent component after successful ticket update:', error);
        // Don't throw the error - the ticket update itself was successful
      }
      
      // Show success message
      console.log('🔧 Ticket update completed successfully');
      
    } catch (error) {
      console.error('❌ Error updating ticket:', error);
      alert(`Failed to update ticket: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Use current ticket or initialTicket as fallback
    const currentTicket = ticket || initialTicket;
    
    if (!currentTicket) {
      console.error('Cannot cancel edit: no ticket data available');
      setIsEditing(false);
      return;
    }
    
    // Reset form to current ticket values
          setEditForm({
        title: currentTicket.title,
        description: currentTicket.description,
        priority: currentTicket.priority,
        status: currentTicket.status,
        customerName: currentTicket.customerName || '',
        customerEmail: currentTicket.customerEmail || '',
        customerPhone: currentTicket.customerPhone || '',
        customerCompany: currentTicket.customerCompany || '',
        customerAddress: currentTicket.customerAddress || '',
        customerStreetAddress: currentTicket.customerStreetAddress || '',
        customerCity: currentTicket.customerCity || '',
        customerState: currentTicket.customerState || '',
        customerZipCode: currentTicket.customerZipCode || '',
        customerCountry: currentTicket.customerCountry || '',
        customerType: currentTicket.customerType || 'Standard',
        deviceModel: currentTicket.deviceModel || '',
        deviceSerialNumber: currentTicket.deviceSerialNumber || '',
    });
    setIsEditing(false);
  };

  const getStatusColor = (status: string) => {
    return getStatusColorClasses(status as any);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMessageTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatStatus = (status: string) => {
    if (status === 'new') {
      return 'New - Not Assigned';
    }
    return getStatusDisplayName(status as any);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleManageWorkflow = () => {
    setShowServiceWorkflow(true);
  };

  const handleCloseWorkflow = () => {
    setShowServiceWorkflow(false);
    // Reload workflow data in case it was created or modified
    loadWorkflow();
  };

  // Available dropdown options - agents should see all available statuses in the correct order
  const statusOptions = getStatusOptions();
  
  const priorityOptions = [
    { value: 'low', label: 'Low', color: getPriorityColor('low') },
    { value: 'medium', label: 'Medium', color: getPriorityColor('medium') },
    { value: 'high', label: 'High', color: getPriorityColor('high') }
  ];
  
  // Function to get category name from ID
  const getCategoryName = (categoryId: string) => {
    if (!categoryId) return 'Unknown';
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return categoryId;
    
    // Handle case where category.name might be an object
    const categoryName = category.name;
    if (typeof categoryName === 'string') {
      return categoryName;
    } else if (typeof categoryName === 'object' && categoryName && (categoryName as any)?.name) {
      return (categoryName as any).name;
    }
    return category.id || 'Unknown Category';
  };

  // Close all dropdowns
  const closeAllDropdowns = () => {
    setShowStatusDropdown(false);
    setShowPriorityDropdown(false);
    setShowCategoryDropdown(false);
    setShowAssignmentDropdown(false);
  };

  // Handlers for dropdown selections
  const handleStatusChange = async (newStatus: string) => {
    console.log('🔧 handleStatusChange called:', { currentStatus: ticket.status, newStatus, hasPermission: user?.permissions?.includes('tickets.edit') });
    if (newStatus !== ticket.status && user?.permissions?.includes('tickets.edit')) {
      console.log('🔧 Calling handleStatusUpdate for status change:', newStatus);
      await handleStatusUpdate(newStatus);
    } else {
      console.log('🔧 Status change skipped:', { 
        sameStatus: newStatus === ticket.status, 
        hasPermission: user?.permissions?.includes('tickets.edit') 
      });
    }
    closeAllDropdowns();
  };

  const handlePriorityChange = async (newPriority: string) => {
    if (newPriority !== ticket.priority && user?.permissions?.includes('tickets.edit')) {
      try {
        const priorityValue = newPriority as 'low' | 'medium' | 'high';
        const response = await apiService.updateTicket(ticket.id, { priority: priorityValue });
        if (response) {
          setTicket(prev => ({ ...prev, priority: priorityValue }));
          onTicketUpdate();
        }
      } catch (error) {
        console.error('Error updating priority:', error);
      }
    }
    closeAllDropdowns();
  };

  const handleCategoryChange = async (newCategoryId: string) => {
    if (newCategoryId !== ticket.category && user?.permissions?.includes('tickets.edit')) {
      try {
        const response = await apiService.updateTicket(ticket.id, { category: newCategoryId });
        if (response) {
          setTicket(prev => ({ ...prev, category: newCategoryId }));
          onTicketUpdate();
        }
      } catch (error) {
        console.error('Error updating category:', error);
      }
    }
    closeAllDropdowns();
  };

  const handleAssignmentChange = async (agentId: string) => {
    try {
      await handleReassignTicket(agentId);
      closeAllDropdowns();
    } catch (error) {
      console.error('Error in handleAssignmentChange:', error);
      // Keep dropdown open on error so user can try again
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-container')) {
        closeAllDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if ticket is not loaded yet
  if (!ticket) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={onBack}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to tickets
          </button>
        </div>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading ticket details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      {/* Back Button - Always at Top */}
      <div className="mb-3">
        <button
          onClick={onBack}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to tickets
        </button>
      </div>

      {/* Main Content - 2 Column Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        {/* LEFT COLUMN - Ticket Details + Service Workflow */}
        <div className="xl:col-span-5 space-y-4">
          {/* Ticket Details - Compact Layout */}
          <div>
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-1.5">
              <div className="flex items-center justify-between mb-1">
                <div className="flex-1">
                <div className="flex items-center space-x-2 flex-wrap">
                  <h1 className="text-base font-bold text-gray-900 dark:text-gray-100">#{ticket.ticketNumber}</h1>
                  
                  {/* Compact Date Badge */}
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                    {formatDate(ticket.createdAt)}
                  </span>

                  {/* Service Workflow Badge */}
                  {workflow && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                      Service
                    </span>
                  )}

                  {/* Compact Status Badge */}
                  <div className="relative dropdown-container">
                    <button
                      onClick={() => {
                        if (user?.permissions?.includes('tickets.edit')) {
                          closeAllDropdowns();
                          setShowStatusDropdown(!showStatusDropdown);
                        }
                      }}
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(ticket.status)} ${user?.permissions?.includes('tickets.edit') ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'} transition-opacity`}
                      disabled={!user?.permissions?.includes('tickets.edit')}
                    >
                      {formatStatus(ticket.status)}
                      {user?.permissions?.includes('tickets.edit') && (
                        <svg className="ml-1 h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>
                    {showStatusDropdown && user?.permissions?.includes('tickets.edit') && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-10">
                        {statusOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleStatusChange(option.value)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${option.value === ticket.status ? 'bg-gray-50 dark:bg-gray-700' : ''}`}
                          >
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${option.color}`}>
                              {option.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Compact Priority Badge */}
                  <div className="relative dropdown-container">
                    <button
                      onClick={() => {
                        if (user?.permissions?.includes('tickets.edit')) {
                          closeAllDropdowns();
                          setShowPriorityDropdown(!showPriorityDropdown);
                        }
                      }}
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 ${user?.permissions?.includes('tickets.edit') ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'} transition-opacity`}
                      disabled={!user?.permissions?.includes('tickets.edit')}
                    >
                      {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                      {user?.permissions?.includes('tickets.edit') && (
                        <svg className="ml-1 h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>
                    {showPriorityDropdown && user?.permissions?.includes('tickets.edit') && (
                      <div className="absolute top-full left-0 mt-1 w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-10">
                        {priorityOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handlePriorityChange(option.value)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${option.value === ticket.priority ? 'bg-gray-50 dark:bg-gray-700' : ''}`}
                          >
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200`}>
                              {option.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Compact Category Badge */}
                  <div className="relative dropdown-container">
                    <button
                      onClick={() => {
                        if (user?.permissions?.includes('tickets.edit')) {
                          closeAllDropdowns();
                          setShowCategoryDropdown(!showCategoryDropdown);
                        }
                      }}
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 ${user?.permissions?.includes('tickets.edit') ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'} transition-opacity`}
                      disabled={!user?.permissions?.includes('tickets.edit')}
                    >
                      {getCategoryName(typeof ticket.category === 'string' ? ticket.category : (ticket.category as any)?.id || '')}
                      {user?.permissions?.includes('tickets.edit') && (
                        <svg className="ml-1 h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>
                    {showCategoryDropdown && user?.permissions?.includes('tickets.edit') && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-10">
                        {categories.map((category) => (
                          <button
                            key={category.id}
                            onClick={() => handleCategoryChange(category.id)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${category.id === (typeof ticket.category === 'string' ? ticket.category : (ticket.category as any)?.id) ? 'bg-gray-50 dark:bg-gray-700' : ''}`}
                          >
                            {(() => {
                              const categoryName = category.name;
                              if (typeof categoryName === 'string') {
                                return categoryName;
                              } else if (typeof categoryName === 'object' && categoryName && (categoryName as any)?.name) {
                                return (categoryName as any).name;
                              }
                              return category.id || 'Unknown Category';
                            })()}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Compact Agent Assignment Badge */}
                  <div className="relative dropdown-container">
                    <button
                      onClick={() => {
                        if (user?.permissions?.includes('tickets.edit')) {
                          closeAllDropdowns();
                          setShowAssignmentDropdown(!showAssignmentDropdown);
                        }
                      }}
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 ${user?.permissions?.includes('tickets.edit') ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'} transition-opacity`}
                      disabled={!user?.permissions?.includes('tickets.edit')}
                    >
                      {ticket.agent ? formatAgentName(ticket.agent) : 'Unassigned'}
                      {user?.permissions?.includes('tickets.edit') && (
                        <svg className="ml-1 h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>
                    {showAssignmentDropdown && user?.permissions?.includes('tickets.edit') && (
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-10">
                        {ticket.agentId && (
                          <button
                            onClick={() => handleAssignmentChange('')}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            ⊘ Unassign Ticket
                          </button>
                        )}
                        {agents.map(agent => {
                          // Check both the agent's base isOnline status and the presence information
                          const isOnlineFromAgent = agent.isOnline || false;
                          const isOnlineFromPresence = agentPresence.get(agent.id) || false;
                          const isOnline = isOnlineFromAgent || isOnlineFromPresence;
                          const statusIndicator = isOnline ? '🟢' : '🔴';
                          
                          return (
                            <button
                              key={agent.id}
                              onClick={() => handleAssignmentChange(agent.id)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${agent.id === ticket.agentId ? 'bg-gray-50 dark:bg-gray-700' : ''}`}
                            >
                              {statusIndicator} {agent.roleName || 'Unknown Role'} - {formatAgentName(agent)}
                              {agent.id === ticket.agentId ? ' (Current)' : ''}
                              {agent.id === user?.id ? ' (You)' : ''}
                              {isOnline ? ' (Online)' : ' (Offline)'}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="ml-4">
                {user?.role === 'agent' && user?.permissions?.includes('tickets.edit') && !isEditing && (
                  <button
                    onClick={() => {
                      console.log('🔧 Edit button clicked - current ticket:', ticket);
                      console.log('🔧 Current ticket deviceModel:', ticket?.deviceModel);
                      console.log('🔧 Current ticket deviceSerialNumber:', ticket?.deviceSerialNumber);
                      console.log('🔧 Current ticket linkedDeviceId:', ticket?.linkedDeviceId);
                      console.log('🔧 Full ticket object keys:', ticket ? Object.keys(ticket) : 'ticket is null');
                      
                      // Populate edit form with current ticket data when entering edit mode
                      if (ticket) {
                        const deviceModel = ticket.deviceModel || '';
                        const deviceSerialNumber = ticket.deviceSerialNumber || '';
                        
                        console.log('🔧 About to set edit form with:');
                        console.log('  - deviceModel:', deviceModel);
                        console.log('  - deviceSerialNumber:', deviceSerialNumber);
                        
                        setEditForm({
                          title: ticket.title,
                          description: ticket.description,
                          priority: ticket.priority,
                          status: ticket.status,
                          customerName: ticket.customerName || '',
                          customerEmail: ticket.customerEmail || '',
                          customerPhone: ticket.customerPhone || '',
                          customerCompany: ticket.customerCompany || '',
                          customerAddress: ticket.customerAddress || '',
                          customerStreetAddress: ticket.customerStreetAddress || '',
                          customerCity: ticket.customerCity || '',
                          customerState: ticket.customerState || '',
                          customerZipCode: ticket.customerZipCode || '',
                          customerCountry: ticket.customerCountry || '',
                          customerType: ticket.customerType || 'Standard',
                          deviceModel: deviceModel,
                          deviceSerialNumber: deviceSerialNumber,
                        });
                        console.log('🔧 Set edit form deviceModel to:', ticket.deviceModel);
                        console.log('🔧 Set edit form deviceSerialNumber to:', ticket.deviceSerialNumber);
                        console.log('🔧 Ticket linkedDeviceId:', ticket.linkedDeviceId);
                        
                        // Set selectedDevice if there's a linkedDeviceId
                        if (ticket.linkedDeviceId && ticket.deviceModel && ticket.deviceSerialNumber) {
                          setSelectedDevice({
                            id: ticket.linkedDeviceId,
                            model: ticket.deviceModel,
                            serialNumber: ticket.deviceSerialNumber
                          });
                          console.log('🔧 Set selectedDevice from linkedDeviceId:', {
                            id: ticket.linkedDeviceId,
                            model: ticket.deviceModel,
                            serialNumber: ticket.deviceSerialNumber
                          });
                        }
                        
                        // Auto-load device list if company name exists
                        if (ticket.customerCompany && ticket.customerCompany.trim() !== '') {
                          console.log('🔧 Company exists in ticket, auto-loading device list for:', ticket.customerCompany);
                          
                          // Use setTimeout to ensure the edit mode is set first
                          setTimeout(async () => {
                            try {
                              // Perform company lookup to get company ID
                              const response = await apiService.fuzzyMatchCompany(ticket.customerCompany!, 75);
                              console.log('🔧 Auto-lookup company response:', response);
                              
                              if (response && response.matches && response.matches.length > 0) {
                                // Find high-confidence match (>=90%)
                                const highConfidenceMatch = response.matches.find((match: any) => match.score >= 90);
                                if (highConfidenceMatch && highConfidenceMatch.id) {
                                  console.log('🔧 Auto-detected company ID:', highConfidenceMatch.id);
                                  setSelectedCompanyId(highConfidenceMatch.id);
                                  // Device list will be loaded automatically via useEffect when selectedCompanyId changes
                                } else {
                                  console.log('🔧 No high-confidence company match found for auto-loading devices');
                                }
                              }
                            } catch (error) {
                              console.error('🔧 Error auto-loading company/devices:', error);
                            }
                          }, 100);
                        }
                      }
                      
                      setIsEditing(true);
                    }}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>

            {isEditing && user?.permissions?.includes('tickets.edit') ? (
              /* Edit Form */
              <div className="space-y-4">
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}
                
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-500 dark:text-gray-400">Title</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={editForm.title}
                    onChange={handleEditFormChange}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-500 dark:text-gray-400">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    value={editForm.description}
                    onChange={handleEditFormChange}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="priority" className="block text-sm font-medium text-gray-500 dark:text-gray-400">Priority</label>
                    <select
                      id="priority"
                      name="priority"
                      value={editForm.priority}
                      onChange={handleEditFormChange}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-500 dark:text-gray-400">Status</label>
                    <select
                      id="status"
                      name="status"
                      value={editForm.status}
                      onChange={handleEditFormChange}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="new">New</option>
                      <option value="in_progress">In Progress</option>
                      <option value="in_progress_ai_agent">In Progress - AI Agent</option>
                      <option value="in_progress_tier1">In Progress - Tier 1</option>
                      <option value="in_progress_tier2">In Progress - Tier 2</option>
                      <option value="in_progress_tier3">In Progress - Tier 3</option>
                      <option value="closed-resolved">Closed - Resolved</option>
                      <option value="closed-not-resolved">Closed - Not Resolved</option>
                      <option value="closed-partially-resolved">Closed - Partially Resolved</option>
                      <option value="closed-no-feedback">Closed - No Feedback</option>
                      <option value="closed-by-agent-resolved">Closed by Agent - Resolved</option>
                      <option value="abandoned">Abandoned</option>
                    </select>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Created (Read Only)</h3>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(ticket.createdAt)}</p>
                </div>

                {/* Customer Information Edit */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Customer Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="customerName" className="block text-xs font-medium text-gray-400 dark:text-gray-500">Name</label>
                      <input
                        type="text"
                        id="customerName"
                        name="customerName"
                        value={editForm.customerName}
                        onChange={handleEditFormChange}
                        className="mt-1 w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="customerEmail" className="block text-xs font-medium text-gray-400 dark:text-gray-500">Email</label>
                      <input
                        type="email"
                        id="customerEmail"
                        name="customerEmail"
                        value={editForm.customerEmail}
                        onChange={handleEditFormChange}
                        className={`mt-1 w-full px-3 py-1 text-sm border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          emailError ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      />
                      {emailError && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{emailError}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="customerPhone" className="block text-xs font-medium text-gray-400 dark:text-gray-500">Phone</label>
                      <input
                        type="tel"
                        id="customerPhone"
                        name="customerPhone"
                        value={editForm.customerPhone}
                        onChange={handleEditFormChange}
                        className="mt-1 w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="relative">
                      <label htmlFor="customerCompany" className="block text-xs font-medium text-gray-400 dark:text-gray-500">Company</label>
                      <input
                        type="text"
                        id="customerCompany"
                        name="customerCompany"
                        value={editForm.customerCompany}
                        onChange={handleEditFormChange}
                        className="mt-1 w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Type company name..."
                      />
                      {showCompanySuggestions && (
                        <CompanySuggestion
                          suggestions={companySuggestions}
                          onSelect={handleCompanySelect}
                          onDismiss={handleDismissCompanySuggestions}
                        />
                      )}
                    </div>
                    <div>
                      <label htmlFor="customerStreetAddress" className="block text-xs font-medium text-gray-400 dark:text-gray-500">Street Address</label>
                      <input
                        type="text"
                        id="customerStreetAddress"
                        name="customerStreetAddress"
                        value={editForm.customerStreetAddress}
                        onChange={handleEditFormChange}
                        className="mt-1 w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div>
                      <label htmlFor="customerCity" className="block text-xs font-medium text-gray-400 dark:text-gray-500">City</label>
                      <input
                        type="text"
                        id="customerCity"
                        name="customerCity"
                        value={editForm.customerCity}
                        onChange={handleEditFormChange}
                        className="mt-1 w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Los Angeles"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="customerState" className="block text-xs font-medium text-gray-400 dark:text-gray-500">State/Province</label>
                        <input
                          type="text"
                          id="customerState"
                          name="customerState"
                          value={editForm.customerState}
                          onChange={handleEditFormChange}
                          className="mt-1 w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="CA"
                        />
                      </div>
                      <div>
                        <label htmlFor="customerZipCode" className="block text-xs font-medium text-gray-400 dark:text-gray-500">Zip/Postal Code</label>
                        <input
                          type="text"
                          id="customerZipCode"
                          name="customerZipCode"
                          value={editForm.customerZipCode}
                          onChange={handleEditFormChange}
                          className="mt-1 w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="90210"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="customerType" className="block text-xs font-medium text-gray-400 dark:text-gray-500">Customer Type</label>
                      <select
                        id="customerType"
                        name="customerType"
                        value={editForm.customerType}
                        onChange={handleEditFormChange}
                        className="mt-1 w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select customer type...</option>
                        {customerTypes.map((type) => (
                          <option key={type.id} value={typeof type.name === 'string' ? type.name : type.id}>
                            {typeof type.name === 'string' ? type.name : type.id}
                          </option>
                        ))}
                        {/* Fallback options if no dropdown options are loaded */}
                        {customerTypes.length === 0 && (
                          <>
                            <option value="Standard">Standard</option>
                            <option value="VIP">VIP</option>
                            <option value="Distributor">Distributor</option>
                          </>
                        )}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="deviceModel" className="block text-xs font-medium text-gray-400 dark:text-gray-500">Device Model</label>
                        <select
                          id="deviceModel"
                          name="deviceModel"
                          value={editForm.deviceModel}
                          onChange={handleEditFormChange}
                          className="mt-1 w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select model...</option>
                          {deviceModels.map((model) => (
                            <option key={model.id} value={typeof model.name === 'string' ? model.name : model.id}>
                              {typeof model.name === 'string' ? model.name : model.id}
                            </option>
                          ))}
                          {/* Fallback options if no dropdown options are loaded */}
                          {deviceModels.length === 0 && (
                            <>
                              <option value="BWIII">BWIII</option>
                              <option value="BWMini">BWMini</option>
                              <option value="Compass">Compass</option>
                              <option value="Maxxi">Maxxi</option>
                            </>
                          )}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="deviceSerialNumber" className="block text-xs font-medium text-gray-400 dark:text-gray-500">Serial Number</label>
                        <input
                          type="text"
                          id="deviceSerialNumber"
                          name="deviceSerialNumber"
                          value={editForm.deviceSerialNumber}
                          onChange={handleEditFormChange}
                          className="mt-1 w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter serial number"
                        />
                      </div>
                    </div>
                    
                    {/* Device Selection from Company */}
                    <div className="col-span-2">
                      <DeviceSelector
                        companyId={selectedCompanyId}
                        selectedDeviceId={selectedDevice?.id || null}
                        onDeviceSelect={handleDeviceSelect}
                        loading={saving}
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="customerCountry" className="block text-xs font-medium text-gray-400 dark:text-gray-500">Country</label>
                      <div className="mt-1">
                        <CountrySelect
                          id="customerCountry"
                          name="customerCountry"
                          value={editForm.customerCountry}
                          onChange={(value) => handleEditFormChange({ target: { name: 'customerCountry', value } } as any)}
                          placeholder="Select a country..."
                          className="text-sm px-3 py-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save/Cancel Buttons */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex space-x-3">
                    <button
                      onClick={handleSaveEdit}
                      disabled={saving}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 py-2 px-4 rounded-md text-sm font-medium hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Display Mode - Ultra Compact Layout */
              <div className="space-y-1">
                {/* Title and Description - Horizontal Layout */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-xs font-medium text-gray-400 dark:text-gray-500 block mb-0.5">Title</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 leading-tight">{ticket.title}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-400 dark:text-gray-500 block mb-0.5">Description</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap leading-tight line-clamp-2" title={ticket.description}>{ticket.description}</p>
                  </div>
                </div>

                {/* Customer & Device Info - Ultra Compact Grid */}
                <div className="pt-1 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Customer & Device Info</h3>
                  
                  {/* All Info in One Grid */}
                  <div className="grid grid-cols-6 gap-2 text-xs">
                    <div>
                      <span className="font-medium text-gray-400 dark:text-gray-500 block mb-0.5">Name</span>
                      <p className="text-gray-900 dark:text-gray-100 truncate" title={
                        (() => {
                          const customerName = ticket.customer?.name || ticket.customerName;
                          if (typeof customerName === 'string') {
                            return customerName;
                          } else if (typeof customerName === 'object' && customerName && (customerName as any)?.name) {
                            return (customerName as any).name;
                          }
                          return 'Not provided';
                        })()
                      }>
                        {(() => {
                          const customerName = ticket.customer?.name || ticket.customerName;
                          if (typeof customerName === 'string') {
                            return customerName;
                          } else if (typeof customerName === 'object' && customerName && (customerName as any)?.name) {
                            return (customerName as any).name;
                          }
                          return 'Not provided';
                        })()}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-400 dark:text-gray-500 block mb-0.5">Email</span>
                      <p className="text-gray-900 dark:text-gray-100 truncate" title={
                        (() => {
                          const customerEmail = ticket.customer?.email || ticket.customerEmail;
                          return typeof customerEmail === 'string' ? customerEmail : 'Not provided';
                        })()
                      }>
                        {(() => {
                          const customerEmail = ticket.customer?.email || ticket.customerEmail;
                          if (typeof customerEmail === 'string') {
                            return customerEmail;
                          } else if (typeof customerEmail === 'object' && customerEmail && (customerEmail as any)?.email) {
                            return (customerEmail as any).email;
                          }
                          return 'Not provided';
                        })()}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-400 dark:text-gray-500 block mb-0.5">Company</span>
                      <p className="text-gray-900 dark:text-gray-100 truncate" title={ticket.customerCompany || 'Not provided'}>
                        {ticket.customerCompany || 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-400 dark:text-gray-500 block mb-0.5">Type</span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        ticket.customerType === 'VIP' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        ticket.customerType === 'Distributor' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {ticket.customerType || 'Standard'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-400 dark:text-gray-500 block mb-0.5">Device</span>
                      <p className="text-gray-900 dark:text-gray-100 truncate" title={ticket.deviceModel || 'Not specified'}>
                        {ticket.deviceModel || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-400 dark:text-gray-500 block mb-0.5">Serial</span>
                      <p className="text-gray-900 dark:text-gray-100 truncate" title={ticket.deviceSerialNumber || 'Not provided'}>
                        {ticket.deviceSerialNumber || 'Not provided'}
                      </p>
                    </div>
                  </div>

                  {/* Address and Country on Second Row if Available */}
                  {(ticket.customerStreetAddress || ticket.customerCity || ticket.customerState || ticket.customerZipCode || ticket.customerAddress || ticket.customerCountry || ticket.customerPhone) && (
                    <div className="grid grid-cols-3 gap-3 text-xs mt-2">
                      {(ticket.customerPhone) && (
                        <div>
                          <span className="font-medium text-gray-400 dark:text-gray-500 block mb-0.5">Phone</span>
                          <p className="text-gray-900 dark:text-gray-100 truncate" title={ticket.customerPhone}>
                            {ticket.customerPhone}
                          </p>
                        </div>
                      )}
                      {(ticket.customerStreetAddress || ticket.customerCity || ticket.customerState || ticket.customerZipCode || ticket.customerAddress) && (
                        <div>
                          <span className="font-medium text-gray-400 dark:text-gray-500 block mb-0.5">Address</span>
                          <div className="text-gray-900 dark:text-gray-100">
                            {(() => {
                              const addressParts = [];
                              if (ticket.customerStreetAddress) addressParts.push(ticket.customerStreetAddress);
                              if (ticket.customerCity) addressParts.push(ticket.customerCity);
                              if (ticket.customerState) addressParts.push(ticket.customerState);
                              if (ticket.customerZipCode) addressParts.push(ticket.customerZipCode);
                              
                              if (addressParts.length > 0) {
                                const fullAddress = addressParts.join(', ');
                                return <p className="truncate" title={fullAddress}>{fullAddress}</p>;
                              } else if (ticket.customerAddress) {
                                return <p className="whitespace-pre-wrap truncate" title={ticket.customerAddress}>{ticket.customerAddress}</p>;
                              } else {
                                return <p className="text-gray-500 dark:text-gray-400">None</p>;
                              }
                            })()}
                          </div>
                        </div>
                      )}
                      {ticket.customerCountry && (
                        <div>
                          <span className="font-medium text-gray-400 dark:text-gray-500 block mb-0.5">Country</span>
                          <p className="text-gray-900 dark:text-gray-100 truncate" title={ticket.customerCountry}>
                            {ticket.customerCountry}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}



            {/* Actions - Compact Layout */}
            {user?.role === 'agent' && (
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                {/* Claim button - only shown when appropriate */}
                {shouldShowClaimButton() && (
                  <button
                    onClick={handleClaimTicket}
                    className="w-full bg-primary-600 text-white py-1.5 px-3 rounded text-sm font-medium hover:bg-primary-700 transition-colors"
                  >
                    {(() => {
                      if (!ticket.agentId || !ticket.agent) return 'Claim Ticket';
                      
                      const agentName = formatAgentName(ticket.agent);
                      return agentName.includes('NeuroAI') || agentName.includes('AI') ? 'Take Over from AI' : 'Claim Ticket';
                    })()}
                  </button>
                )}

                {ticket.agentId === user.id && user?.permissions?.includes('tickets.edit') && !isTicketClosed(ticket.status) && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleStatusUpdate('in_progress')}
                      disabled={ticket.status === 'in_progress'}
                      className="flex-1 bg-yellow-600 text-white py-1.5 px-3 rounded text-sm font-medium hover:bg-yellow-700 transition-colors disabled:opacity-50"
                    >
                      In Progress
                    </button>
                    <button
                      onClick={() => handleStatusUpdate('closed-resolved')}
                      className="flex-1 bg-green-600 text-white py-1.5 px-3 rounded text-sm font-medium hover:bg-green-700 transition-colors"
                    >
                      Resolved
                    </button>
                    {user?.permissions?.includes('tickets.edit') && (
                      <div className="flex items-center space-x-1.5 px-2 py-1.5 bg-gray-50 dark:bg-gray-800 rounded">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">AI</span>
                        <div className={`w-1.5 h-1.5 rounded-full ${aiEnabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <button
                          onClick={() => handleToggleAi(!aiEnabled, aiEnabled ? 'manual' : undefined)}
                          disabled={aiToggling}
                          className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none ${
                            aiEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                          } ${aiToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${
                              aiEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {ticket.agentId === user.id && user?.permissions?.includes('tickets.edit') && isTicketClosed(ticket.status) && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleStatusUpdate('in_progress')}
                      className="flex-1 bg-blue-600 text-white py-1.5 px-3 rounded text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                      Reopen
                    </button>
                    {user?.permissions?.includes('tickets.edit') && (
                      <div className="flex items-center space-x-1.5 px-2 py-1.5 bg-gray-50 dark:bg-gray-800 rounded">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">AI</span>
                        <div className={`w-1.5 h-1.5 rounded-full ${aiEnabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <button
                          onClick={() => handleToggleAi(!aiEnabled, aiEnabled ? 'manual' : undefined)}
                          disabled={aiToggling}
                          className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none ${
                            aiEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                          } ${aiToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${
                              aiEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Compact AI Status Indicator */}
                {user?.permissions?.includes('tickets.edit') && (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {aiEnabled ? (
                      <span className="text-green-600 dark:text-green-400">✓ AI active</span>
                    ) : (
                      <span className="text-red-600 dark:text-red-400">✗ AI disabled</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

          {/* Service Workflow Section */}
          <div>
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-2">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Service Workflow</h3>
                {user?.permissions?.includes('tickets.edit') && ticket.deviceSerialNumber && (
                  <button 
                    onClick={handleManageWorkflow}
                    className="px-3 py-1 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                  >
                    {workflow ? 'Manage Workflow' : 'Create Workflow'}
                  </button>
                )}
              </div>
              
              {loadingWorkflow ? (
                <div className="text-center py-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading workflow...</p>
                </div>
              ) : workflow ? (
                <div className="space-y-3">
                  {/* Workflow Info */}
                  <div className="grid grid-cols-2 gap-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Workflow #</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{workflow.workflowNumber}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        workflow.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        workflow.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                        {workflow.status.replace('_', ' ').charAt(0).toUpperCase() + workflow.status.replace('_', ' ').slice(1)}
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-1">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: workflow.steps ? 
                            `${(workflow.steps.filter((s: any) => s.status === 'completed').length / workflow.steps.length) * 100}%`
                            : '0%'
                        }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>Progress</span>
                      <span>
                        {workflow.steps ? 
                          `${workflow.steps.filter((s: any) => s.status === 'completed').length} / ${workflow.steps.length} completed`
                          : '0 / 0 completed'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-3">
                  <svg className="mx-auto h-6 w-6 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">No Service Workflow</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {ticket.deviceSerialNumber 
                      ? 'Click "Create Workflow" to create a service workflow for device tracking'
                      : 'Add device information to enable service workflow management'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* AI Summary Section */}
          <div>
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-2">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">AI Summary</h3>
                {user?.permissions?.includes('tickets.edit') && (
                  <button
                    onClick={() => {
                      console.log('🔵 [DEBUG] Button clicked!');
                      console.log('🔵 [DEBUG] Current generatingSummary state:', generatingSummary);
                      handleGenerateSummary();
                    }}
                    disabled={generatingSummary}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {generatingSummary ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        <span>Generating...</span>
                      </div>
                    ) : ticket.resolutionSummary ? (
                      'Regenerate'
                    ) : (
                      'Generate Summary'
                    )}
                  </button>
                )}
              </div>
              
              {ticket.resolutionSummary ? (
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap text-sm">{ticket.resolutionSummary}</p>
                  </div>
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>
                      Generated {ticket.resolutionSummaryGeneratedAt ? 
                        new Date(ticket.resolutionSummaryGeneratedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Unknown'}
                    </span>
                    {ticket.resolutionSummaryModelVersion && (
                      <span className="text-gray-400 dark:text-gray-500 font-mono text-xs">
                        {ticket.resolutionSummaryModelVersion}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <svg className="mx-auto h-8 w-8 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-gray-500 dark:text-gray-400 mb-2 text-sm">No AI summary available</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {user?.permissions?.includes('tickets.edit') 
                      ? 'Click "Generate Summary" to create an AI-powered summary of this ticket'
                      : 'Summaries are automatically generated when tickets are closed'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - Chat + Quick Messages */}
        <div className="xl:col-span-7 flex flex-col lg:flex-row space-y-3 lg:space-y-0 lg:space-x-3">
          {/* Chat Section */}
          <div className="flex-1">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg flex flex-col" style={{ height: 'calc(100vh - 180px)', minHeight: '600px' }}>
            <div className="p-2 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <h2 className="text-base font-medium text-gray-900 dark:text-gray-100">
                    {activeTab === 'messages' ? 'Conversation' : 'Internal Comments'}
                  </h2>
                  {/* Customer Status Indicator - Only show for agents and in messages tab */}
                  {user?.role === 'agent' && activeTab === 'messages' && (
                    <div className="flex items-center space-x-2">
                      <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                        customerStatus.isOnline 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                          : customerStatus.hasEverConnected
                            ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          customerStatus.isOnline 
                            ? 'bg-green-500'
                            : customerStatus.hasEverConnected
                              ? 'bg-gray-500'
                              : 'bg-yellow-500'
                        }`}></div>
                        <span>
                          {customerStatus.isOnline 
                            ? 'Customer Online'
                            : customerStatus.hasEverConnected
                              ? `Offline (${customerStatus.lastSeen ? new Date(customerStatus.lastSeen).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : 'Unknown'})`
                              : 'Never Connected'
                          }
                        </span>
                      </div>
                      
                      {/* Email Mode Indicator */}
                      {isEmailMode && (
                        <div className="flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>Email Mode Active</span>
                          {emailModeReason && (
                            <span className="opacity-75">({emailModeReason.replace('_', ' ')})</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
{(user?.role === 'agent' || user?.userType === 'agent') && (
                  <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    <button
                      onClick={() => setActiveTab('messages')}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        activeTab === 'messages'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      Messages
                    </button>
                    <button
                      onClick={() => setActiveTab('comments')}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        activeTab === 'comments'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      Comments ({internalComments.length})
                    </button>
                    <button
                      onClick={() => {
                        setActiveTab('files');
                        // Force reload files when clicking the tab
                        console.log('📁 Files tab clicked, forcing reload...');
                        loadFiles();
                      }}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        activeTab === 'files'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      Files ({files.length})
                    </button>
                    <button
                      onClick={() => {
                        setActiveTab('emails');
                        // Force reload emails when clicking the tab
                        console.log('📧 Emails tab clicked, forcing reload...');
                        loadEmails();
                      }}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        activeTab === 'emails'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      Emails ({emails.length})
                    </button>
                  </div>
                )}
              </div>
            </div>

            {activeTab === 'emails' ? (
              // Emails Tab Content
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-4">
                  {emails.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <svg className="mx-auto h-12 w-12 mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} 
                              d="M3 8l7.89 3.26a2 2 0 001.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm">No emails found for this ticket</p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                          Email History ({emails.length})
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          All email communications related to this ticket
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        {emails.map((email) => (
                          <div key={email.id} 
                               className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    email.type === 'outgoing' 
                                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                                      : email.type === 'incoming'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                  }`}>
                                    {email.type === 'outgoing' ? '📤 Sent' : email.type === 'incoming' ? '📥 Received' : '❌ Error'}
                                  </span>
                                  
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    email.status === 'sent' || email.status === 'processed'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                      : email.status === 'failed'
                                      ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                                  }`}>
                                    {email.status}
                                  </span>
                                </div>
                                
                                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                                  {email.subject}
                                </h4>
                                
                                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                                  <div><strong>From:</strong> {email.fromEmail}</div>
                                  <div><strong>To:</strong> {email.toEmail}</div>
                                  <div><strong>Date:</strong> {new Date(email.createdAt).toLocaleString()}</div>
                                  {email.sentAt && (
                                    <div><strong>Sent:</strong> {new Date(email.sentAt).toLocaleString()}</div>
                                  )}
                                </div>
                              </div>
                              
                              <button
                                onClick={() => {
                                  // Toggle email content visibility (implement expand/collapse)
                                  const emailDiv = document.getElementById(`email-content-${email.id}`);
                                  if (emailDiv) {
                                    emailDiv.style.display = emailDiv.style.display === 'none' ? 'block' : 'none';
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                              >
                                View Content
                              </button>
                            </div>
                            
                            {/* Email content (hidden by default) */}
                            <div id={`email-content-${email.id}`} style={{ display: 'none' }} 
                                 className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded text-sm">
                                <div dangerouslySetInnerHTML={{ __html: email.content }} />
                              </div>
                            </div>
                            
                            {/* Error message if present */}
                            {email.errorMessage && (
                              <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded">
                                <p className="text-sm text-red-800 dark:text-red-400">
                                  <strong>Error:</strong> {email.errorMessage}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : activeTab === 'messages' ? (
              <>
                {/* Unified Messages and Emails View */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {loading ? (
                    <div className="flex justify-center items-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  ) : (!messages || messages.length === 0) && (!emails || emails.length === 0) ? (
                    <div className="text-center text-gray-500 py-8">
                      <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    <>
                      {(messages || []).map((message) => {
                        // Agent-specific message classification
                        const sender = (message as any).sender;
                        const senderUserType = sender?.userType;
                        
                        // Check if this is an email message (sent via email API or system email notifications)
                        const isEmailMessage = message.content?.includes('📧 Email sent to customer:') || 
                                              message.content?.includes('📧 Email sent to customer by') ||
                                              message.content?.includes('📧 Quick email sent to customer:') ||
                                              message.content?.includes('Email mode activated');
                        
                        // In agent view, determine message type based on current user and sender
                        let isSystemMessage = false;
                        let isOwnMessage = false;
                        let isCustomerMessage = false;
                        let isAgentMessage = false;
                        let isAIMessage = false;
                        
                        // System messages
                        if (senderUserType === 'system' || message.messageType === 'system') {
                          isSystemMessage = true;
                        }
                        // Customer messages (including anonymous users)
                        else if (senderUserType === 'customer' || (!sender || (!message.userId && !message.senderId))) {
                          isCustomerMessage = true;
                        }
                        // AI messages
                        else if (senderUserType === 'ai') {
                          isAIMessage = true;
                        }
                        // Agent messages (including current user if they are an agent)
                        else if (senderUserType === 'agent') {
                          // Check if this is the current user's message
                          if (user && (
                            message.userId === user.id ||
                            message.senderId === user.id ||
                            sender?.id === user.id
                          )) {
                            isOwnMessage = true;
                          } else {
                            isAgentMessage = true;
                          }
                        }
                        // Default: treat as agent message if we can't determine the type
                        else {
                          isAgentMessage = true;
                        }
                        
                        // Check if this is a customer email message (received via email)
                        const isCustomerEmailMessage = isCustomerMessage && message.messageType === 'email';
                        
                        // Get sender name for display
                        const senderName = sender && sender.firstName && sender.lastName 
                          ? `${sender.firstName} ${sender.lastName}`.trim()
                          : sender?.name || null;
                        
                        // Debug logging
                        console.log('🔍 TicketDetail Message debug:', {
                          messageId: message.id,
                          content: message.content.substring(0, 20) + '...',
                          sender: sender,
                          senderUserType: senderUserType,
                          isOwnMessage: isOwnMessage,
                          isCustomerMessage: isCustomerMessage,
                          isAgentMessage: isAgentMessage,
                          isSystemMessage: isSystemMessage,
                          senderName: senderName,
                          currentUserId: user?.id,
                          messageUserId: message.userId,
                          messageSenderId: message.senderId,
                          senderIdMatches: sender?.id === user?.id,
                          classification: isOwnMessage ? 'OWN' : isCustomerMessage ? 'CUSTOMER' : isAgentMessage ? 'AGENT' : 'OTHER'
                        });
                        
                        // Handle email notification messages differently
                        if (message.messageType === 'email_notification') {
                          return (
                            <div key={message.id} className="flex justify-center my-2">
                              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2 max-w-md cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                                   onClick={() => setActiveTab('emails')}>
                                <div className="flex items-center space-x-2">
                                  <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  <p className="text-sm text-blue-800 dark:text-blue-200 break-words whitespace-pre-wrap">{message.content}</p>
                                </div>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 text-center">
                                  {formatMessageTime(message.createdAt)} • Click to view email
                                </p>
                              </div>
                            </div>
                          );
                        }

                        // Handle email mode confirmation messages (purple style)
                        if (message.messageType === 'email') {
                          return (
                            <div key={message.id} className="flex justify-center my-2">
                              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg px-4 py-2 max-w-md">
                                <div className="flex items-center space-x-2">
                                  <svg className="h-4 w-4 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  <p className="text-sm text-purple-800 dark:text-purple-200 break-words whitespace-pre-wrap">{message.content}</p>
                                </div>
                                <p className="text-xs text-purple-600 dark:text-purple-400 mt-1 text-center">
                                  {formatMessageTime(message.createdAt)}
                                </p>
                              </div>
                            </div>
                          );
                        }

                        // Email history messages are now displayed only in the dedicated Emails tab
                        // to avoid duplication and provide better functionality (expandable content, status, etc.)
                        
                        // Handle system messages differently
                        if (isSystemMessage) {
                          // Check if this is an email confirmation message
                          const isEmailConfirmation = message.content.includes('📧 Email sent to customer:') || 
                                                     message.content.includes('📧 Email sent to customer by');
                          
                          if (isEmailConfirmation) {
                            // Style email confirmations with purple styling and make clickable
                            return (
                              <div key={message.id} className="flex justify-center my-2">
                                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg px-4 py-2 max-w-md cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                                     onClick={() => setActiveTab('emails')}>
                                  <div className="flex items-center space-x-2">
                                    <svg className="h-4 w-4 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    <p className="text-sm text-purple-800 dark:text-purple-200 break-words whitespace-pre-wrap">{message.content}</p>
                                  </div>
                                  <p className="text-xs text-purple-600 dark:text-purple-400 mt-1 text-center">
                                    {formatMessageTime(message.createdAt)} • Click to view email
                                  </p>
                                </div>
                              </div>
                            );
                          } else {
                            // Regular system messages with yellow styling
                            return (
                              <div key={message.id} className="flex justify-center my-2">
                                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-4 py-2 max-w-md">
                                  <div className="flex items-center space-x-2">
                                    <svg className="h-4 w-4 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-sm text-yellow-800 dark:text-yellow-200 break-words whitespace-pre-wrap">{message.content}</p>
                                  </div>
                                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1 text-center">
                                    {formatMessageTime(message.createdAt)}
                                  </p>
                                </div>
                              </div>
                            );
                          }
                        }
                        
                        // Handle customer email messages with compact format
                        if (isCustomerEmailMessage) {
                          // Create a very short preview - only first few words
                          const words = message.content.trim().split(/\s+/);
                          const previewContent = words.length > 6 
                            ? words.slice(0, 6).join(' ') + '...' 
                            : message.content;
                          
                                                   return (
                            <div key={message.id} className="flex justify-start my-2">
                              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2 max-w-sm cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                                   onClick={() => setActiveTab('emails')}>
                                <div className="flex items-center space-x-2">
                                  <svg className="h-4 w-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  <div className="flex-1">
                                    <p className="text-xs text-green-700 dark:text-green-300 font-medium">📧 Received Email</p>
                                    <p className="text-sm text-green-800 dark:text-green-200 break-words">{previewContent}</p>
                                  </div>
                                </div>
                                <div className="mt-1 text-center">
                                  <p className="text-xs text-green-600 dark:text-green-400">
                                    {formatMessageTime(message.createdAt)}
                                  </p>
                                  <p className="text-xs text-green-700 dark:text-green-300 font-medium underline">
                                    Click to view full email →
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        return (
                          <div
                            key={message.id}
                            className={`flex ${(isOwnMessage || isAgentMessage) ? 'justify-end' : 'justify-start'} mb-2`}
                          >
                            <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                              isEmailMessage
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100 border border-purple-200 dark:border-purple-800 rounded-bl-sm'
                                : isOwnMessage || isAgentMessage
                                ? 'bg-blue-600 text-white rounded-br-sm'
                                : isCustomerMessage
                                ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-900 dark:text-orange-100 border border-orange-200 dark:border-orange-800 rounded-bl-sm'
                                : isAIMessage
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-100 border border-green-200 dark:border-green-800 rounded-bl-sm'
                                : 'bg-gray-100 dark:bg-gray-900/30 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-800 rounded-bl-sm'
                            }`}>
                              <MessageContentRenderer 
                                content={message.content} 
                                className="text-sm break-words"
                              />
                              <p className={`text-xs mt-1 ${
                                isEmailMessage
                                  ? 'text-purple-700 dark:text-purple-300'
                                  : isOwnMessage || isAgentMessage
                                  ? 'text-blue-100' 
                                  : isCustomerMessage
                                  ? 'text-orange-700 dark:text-orange-300'
                                  : isAIMessage
                                  ? 'text-green-700 dark:text-green-300'
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}>
                                {(() => {
                                  if (isEmailMessage) return 'Email';
                                  if (isOwnMessage) return senderName || 'You';
                                  if (isCustomerMessage) return 'Customer';
                                  if (isAIMessage) return senderName || 'NeuroAI';
                                  if (isAgentMessage) return senderName || 'Agent';
                                  return senderName || 'Support Agent';
                                })()} • {formatMessageTime(message.createdAt)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      
                      {isTyping && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 text-gray-900 px-4 py-2 rounded-lg">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                {user?.permissions?.includes('tickets.message') ? (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                    {/* File Upload Area */}
                    {showFileUpload && (
                      <div className="mb-4">
                        <FileUpload
                          onFileSelect={handleFileSelect}
                          disabled={uploadingFiles.length > 0}
                          multiple={true}
                          className="w-full"
                        />
                        {uploadingFiles.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {uploadingFiles.map((file, index) => (
                              <div key={index} className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600"></div>
                                <span>Uploading {file.name}...</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <form onSubmit={handleSendMessage} className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => setShowFileUpload(!showFileUpload)}
                        disabled={uploadingFiles.length > 0}
                        className={`rounded-md transition-colors px-3 py-2 ${
                          showFileUpload 
                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title="Attach files"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                      </button>
                      <input
                        type="text"
                        value={newMessage}
                        onChange={handleMessageInputChange}
                        placeholder={isEmailMode ? "Type email message..." : "Type your message..."}
                        disabled={sendingMessage || uploadingFiles.length > 0}
                        className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                          isEmailMode
                            ? 'border-purple-300 dark:border-purple-600 focus:ring-purple-500 focus:border-purple-500'
                            : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500'
                        }`}
                      />
                      <button
                        type="submit"
                        disabled={sendingMessage || !newMessage.trim() || uploadingFiles.length > 0}
                        className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                          isEmailMode 
                            ? 'bg-purple-600 text-white hover:bg-purple-700 focus:ring-purple-500' 
                            : 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500'
                        }`}
                        title={isEmailMode ? "Send Email" : "Send Message"}
                      >
                        {sendingMessage ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <div className="flex items-center space-x-1">
                            {isEmailMode ? (
                              <>
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <span>Email</span>
                              </>
                            ) : (
                              'Send'
                            )}
                          </div>
                        )}
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="p-4 border-t border-gray-200 bg-gray-50 dark:bg-gray-800">
                    <div className="text-center text-gray-500 dark:text-gray-400">
                      <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <p className="text-sm">You don't have permission to send messages</p>
                    </div>
                  </div>
                )}
              </>
            ) : activeTab === 'comments' ? (
              <>
                {/* Internal Comments */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {internalComments.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012 2h14a2 2 0 012 2v8a2 2 0 01-2 2h-1.586l-4.414 4.414z" />
                      </svg>
                      <p>No internal comments yet. Add one to start tracking progress!</p>
                    </div>
                  ) : (
                    internalComments.map((comment) => (
                      <div key={comment.id} className="flex justify-start">
                        <div className="max-w-full px-4 py-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                          <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{comment.content}</p>
                          <p className="text-xs mt-2 text-gray-600 dark:text-gray-400">
                            <span className="font-medium">{comment.agentName}</span> • {formatDate(comment.createdAt)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Comment Input */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  <form onSubmit={handleAddComment} className="space-y-3">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add an internal comment (not visible to customer)..."
                      disabled={addingComment}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                    />
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Internal comments are only visible to agents
                      </p>
                      <button
                        type="submit"
                        disabled={addingComment || !newComment.trim()}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {addingComment ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          'Add Comment'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </>
            ) : (
              <>
                {/* Files */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {files.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <p>No files uploaded yet.</p>
                      <button
                        onClick={() => {
                          console.log('🔄 Manual refresh files button clicked');
                          loadFiles();
                        }}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        🔄 Refresh Files
                      </button>
                      <div className="mt-2 text-xs text-gray-400">
                        Ticket ID: {ticket?.id}<br/>
                        Files count: {files.length}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {files.map((file) => {
                        const getFileIcon = (messageType: string, fileName: string) => {
                          if (messageType === 'image') {
                            return (
                              <svg className="w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            );
                          }
                          
                          const extension = fileName.split('.').pop()?.toLowerCase();
                          switch (extension) {
                            case 'pdf':
                              return (
                                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              );
                            case 'doc':
                            case 'docx':
                              return (
                                <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              );
                            case 'xls':
                            case 'xlsx':
                            case 'csv':
                              return (
                                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                              );
                            case 'zip':
                            case 'rar':
                            case '7z':
                              return (
                                <svg className="w-8 h-8 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                              );
                            default:
                              return (
                                <svg className="w-8 h-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              );
                          }
                        };

                        const formatFileSize = (bytes: number) => {
                          if (bytes === 0) return '0 Bytes';
                          const k = 1024;
                          const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                          const i = Math.floor(Math.log(bytes) / Math.log(k));
                          return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
                        };

                        return (
                          <div key={file.id} className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="flex-shrink-0 mr-4">
                              {getFileIcon(file.messageType, file.fileName)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {file.fileName}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatFileSize(file.fileSize)} • Uploaded by{' '}
                                    <span className="font-medium">
                                      {file.sender.firstName} {file.sender.lastName}
                                    </span>{' '}
                                    ({file.sender.userType}) • {formatDate(file.uploadedAt)}
                                  </p>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {file.messageType === 'image' && (
                                    <button
                                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}${file.fileUrl}`, '_blank')}
                                      className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                                    >
                                      Preview
                                    </button>
                                  )}
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/files/${file.filePath.split('/').pop()}`}
                                    download={file.fileName}
                                    className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                                  >
                                    Download
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          </div>

          {/* Quick Messages Panel */}
          {user?.role === 'agent' && activeTab === 'messages' && (
            <QuickMessages
              onSendMessage={handleQuickSendMessage}
              onInsertMessage={handleQuickInsertMessage}
              disabled={sendingMessage || !user?.permissions?.includes('tickets.edit') || isTicketClosed(ticket.status)}
            />
          )}
        </div>
      </div>
      
      {/* Service Workflow Modal */}
      {showServiceWorkflow && (
        <ServiceWorkflow
          ticketId={ticket.id}
          deviceSerialNumber={ticket.deviceSerialNumber}
          ticket={ticket}
          onClose={handleCloseWorkflow}
        />
      )}
    </div>
  );
};

export default TicketDetail; 